/**
 * TrainingProfileEditor Page
 * Provides profile metadata editing, visual configuration editing, DB-backed tutorial
 * quest/step authoring with RichContent, quiz assignment selection, and ownership tools.
 *
              label: t('trainingProfiles.spotlightTargetLabel', 'Spotlight target'),
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  AimOutlined,
  ApartmentOutlined,
  ArrowDownOutlined,
  ArrowLeftOutlined,
  ArrowUpOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CodeOutlined,
  CopyOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FilePdfOutlined,
  InfoCircleOutlined,
  ImportOutlined,
  PictureOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SaveOutlined,
  ShareAltOutlined,
  UploadOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import PagesWrapper from '../../PagesWrapper';
import RichTextEditor from '../../components/RichTextEditor';
import { AccessBanner, OwnershipManager } from '../../components/Ownership';
import { useOwnership } from '../../hooks/useOwnership';
import countriesService from '../../services/countriesService';
import trainingProfilesService from '../../services/trainingProfilesService';
import {
  buildTrainingProfileFixedValues,
  buildTrainingProfileSimulationDefaults,
  buildTrainingProfileUiConfiguration,
  createTrainingProfileConfigurationDraft,
  TRAINING_PROFILE_CONFIGURATION_GROUPS,
} from './trainingProfileConfiguration';
import TrainingProfileBulkImportModal from './TrainingProfileBulkImportModal';
import { AVAILABLE_QUEST_ICONS, renderQuestIcon } from '../Simulations/HeatPumpSimulation/questIcons';
import { AVAILABLE_SPOTLIGHT_SELECTORS } from '../Simulations/HeatPumpSimulation/spotlightTargets';
import styles from './TrainingProfileEditor.module.css';

const { TextArea } = Input;
const DEFAULT_CONDITION = {
  combinator: 'and',
  rules: [
    {
      field: 'outdoorTemperature',
      operator: 'lessThanOrEqual',
      value: -10,
      label: 'Outdoor temperature ≤ -10°C',
    },
  ],
};

const CROSS_FIELD_CONDITION = {
  combinator: 'and',
  rules: [
    {
      field: 'supplyTemperature',
      operator: 'greaterThanOrEqualFieldMultiplier',
      compareField: 'outdoorTemperature',
      multiplier: -2,
      label: 'Supply temperature is at least 2x the absolute outdoor temperature',
    },
  ],
};

const TWO_CONDITION_EXAMPLE = {
  combinator: 'and',
  rules: [
    {
      field: 'outdoorTemperature',
      operator: 'lessThanOrEqual',
      value: -5,
      label: 'Outdoor temperature <= -5C',
    },
    {
      field: 'qNet',
      operator: 'greaterThanOrEqual',
      value: 0,
      label: 'Q-net is non-negative',
    },
  ],
};

const MODAL_EDITOR_HEIGHT_PROPS = Object.freeze({
  compactToolbar: true,
  maxLength: 5000,
  height: 220,
  minHeight: 140,
  maxHeight: 320,
});

const SPOTLIGHT_BUILDER_DEFAULTS = Object.freeze({
  selector: undefined,
  position: 'right',
  displayName: '',
  useFallbackRect: false,
  fallbackX: undefined,
  fallbackY: undefined,
  fallbackWidth: undefined,
  fallbackHeight: undefined,
});

const SPOTLIGHT_POSITION_DEFINITIONS = Object.freeze([
  { key: 'top', description: 'Places the spotlight callout above the matched element.' },
  { key: 'bottom', description: 'Places the spotlight callout below the matched element.' },
  { key: 'left', description: 'Places the spotlight callout to the left of the matched element.' },
  { key: 'right', description: 'Places the spotlight callout to the right of the matched element.' },
]);

const CONDITION_COMBINATOR_DEFINITIONS = Object.freeze([
  { key: 'and', description: 'Every child rule in the group must pass.' },
  { key: 'or', description: 'At least one child rule in the group must pass.' },
]);

const CONDITION_OPERATOR_DEFINITIONS = Object.freeze([
  { key: 'equals', example: '{ "value": 5 }' },
  { key: 'notEquals', example: '{ "value": "living" }' },
  { key: 'greaterThan', example: '{ "value": 45 }' },
  { key: 'greaterThanOrEqual', example: '{ "value": 45 }' },
  { key: 'lessThan', example: '{ "value": 10 }' },
  { key: 'lessThanOrEqual', example: '{ "value": -10 }' },
  { key: 'between', example: '{ "value": [35, 45] }' },
  { key: 'in', example: '{ "value": ["living", "bathroom"] }' },
  { key: 'contains', example: '{ "value": "room" }' },
  { key: 'approximately', example: '{ "value": 50, "tolerance": 0.5 }' },
  { key: 'greaterThanOrEqualFieldMultiplier', example: '{ "compareField": "outdoorTemperature", "multiplier": -2 }' },
  { key: 'lessThanOrEqualFieldMultiplier', example: '{ "compareField": "heatOutput", "multiplier": 1.1 }' },
]);

const CONDITION_FIELD_DEFINITIONS = Object.freeze([
  { key: 'isSimulationRunning', label: 'Simulation running', type: 'boolean' },
  { key: 'outdoorTemperature', label: 'Outdoor temperature', type: 'number' },
  { key: 'selectedRoomType', label: 'Selected room type', type: 'string' },
  { key: 'desiredTemperature', label: 'Desired room temperature', type: 'number' },
  { key: 'supplyTemperature', label: 'Supply temperature', type: 'number' },
  { key: 'heatOutput', label: 'Heat output', type: 'number' },
  { key: 'qNet', label: 'Q-net', type: 'number' },
  { key: 'envelopeLoss', label: 'Envelope loss', type: 'number' },
  { key: 'ventilationLoss', label: 'Ventilation loss', type: 'number' },
  { key: 'totalHeatLoss', label: 'Total heat loss', type: 'number' },
  { key: 'airChangeRate', label: 'Air change rate', type: 'number' },
  { key: 'currentCOP', label: 'Current COP', type: 'number' },
  { key: 'currentTemperature', label: 'Current room temperature', type: 'number' },
  { key: 'valvePosition', label: 'Valve position', type: 'number' },
  { key: 'thermostatLevel', label: 'Thermostat level', type: 'number' },
  { key: 'radiatorType', label: 'Radiator type', type: 'string' },
  { key: 'radiatorCount', label: 'Radiator count', type: 'number' },
  { key: 'systemPressure', label: 'System pressure', type: 'number' },
  { key: 'uValue', label: 'U-value', type: 'number' },
  { key: 'totalArea', label: 'Total area', type: 'number' },
  { key: 'roofArea', label: 'Roof area', type: 'number' },
  { key: 'wallArea', label: 'Wall area', type: 'number' },
  { key: 'windowArea', label: 'Window area', type: 'number' },
  { key: 'selectedCity', label: 'Selected city', type: 'string' },
]);

const CONDITION_FIELD_MAP = Object.freeze(Object.fromEntries(
  CONDITION_FIELD_DEFINITIONS.map((field) => [field.key, field])
));

const NUMERIC_CONDITION_FIELDS = new Set(
  CONDITION_FIELD_DEFINITIONS.filter(field => field.type === 'number').map(field => field.key)
);

const BOOLEAN_CONDITION_FIELDS = new Set(
  CONDITION_FIELD_DEFINITIONS.filter(field => field.type === 'boolean').map(field => field.key)
);

const CONDITION_OPERATOR_SET = new Set(CONDITION_OPERATOR_DEFINITIONS.map(operator => operator.key));
const CONDITION_FIELD_SET = new Set(CONDITION_FIELD_DEFINITIONS.map(field => field.key));
const SPOTLIGHT_POSITION_SET = new Set(SPOTLIGHT_POSITION_DEFINITIONS.map(position => position.key));

const getRequiredStringProperty = (node, key) => {
  const value = node?.[key];
  return typeof value === 'string' && value.trim() ? value : null;
};

const validateConditionValue = (field, operator, value) => {
  if (operator === 'between') {
    if (!Array.isArray(value) || value.length !== 2) {
      return 'The between operator requires a two-item value array.';
    }
    if (NUMERIC_CONDITION_FIELDS.has(field) && value.some(item => typeof item !== 'number')) {
      return `The between operator for '${field}' requires numeric values.`;
    }
    return null;
  }

  if (operator === 'in') {
    return Array.isArray(value) ? null : 'The in operator requires an array value.';
  }

  if (NUMERIC_CONDITION_FIELDS.has(field) && typeof value !== 'number') {
    return `Condition field '${field}' requires a numeric value.`;
  }

  if (BOOLEAN_CONDITION_FIELDS.has(field) && typeof value !== 'boolean') {
    return `Condition field '${field}' requires a boolean value.`;
  }

  if (!NUMERIC_CONDITION_FIELDS.has(field) && !BOOLEAN_CONDITION_FIELDS.has(field)
    && typeof value !== 'string' && !Array.isArray(value)) {
    return `Condition field '${field}' requires a string or array value.`;
  }

  return null;
};

const validateConditionNode = (node, depth, state) => {
  if (depth > 8) return 'Condition AST exceeds maximum nesting depth of 8.';
  if (!node || typeof node !== 'object' || Array.isArray(node)) return 'Condition AST nodes must be objects.';

  if (Array.isArray(node.rules)) {
    const combinator = String(node.combinator || node.condition || 'and').toLowerCase();
    if (combinator !== 'and' && combinator !== 'or') {
      return "Condition groups must use combinator 'and' or 'or'.";
    }

    for (const child of node.rules) {
      const error = validateConditionNode(child, depth + 1, state);
      if (error) return error;
    }

    return null;
  }

  state.ruleCount += 1;
  if (state.ruleCount > 100) return 'Condition AST cannot contain more than 100 rules.';

  const field = getRequiredStringProperty(node, 'field');
  if (!field || !CONDITION_FIELD_SET.has(field)) {
    return `Condition field '${field || '<missing>'}' is not allowed.`;
  }

  const operator = getRequiredStringProperty(node, 'operator');
  if (!operator || !CONDITION_OPERATOR_SET.has(operator)) {
    return `Condition operator '${operator || '<missing>'}' is not allowed.`;
  }

  if (operator.includes('FieldMultiplier')) {
    const compareField = getRequiredStringProperty(node, 'compareField');
    if (!compareField || !CONDITION_FIELD_SET.has(compareField) || !NUMERIC_CONDITION_FIELDS.has(compareField)) {
      return 'Cross-field multiplier conditions require an allowed numeric compareField.';
    }

    if (typeof node.multiplier !== 'number') {
      return 'Cross-field multiplier conditions require a numeric multiplier.';
    }

    return null;
  }

  if (!Object.prototype.hasOwnProperty.call(node, 'value')) {
    return 'Condition rule requires a value.';
  }

  return validateConditionValue(field, operator, node.value);
};

const validateConditionAstValue = (conditionAst) => {
  if (conditionAst === null || conditionAst === undefined) return null;

  const state = { ruleCount: 0 };
  return validateConditionNode(conditionAst, 0, state);
};

const summarizeConditionAst = (conditionAst) => {
  if (!conditionAst || typeof conditionAst !== 'object') {
    return {
      ruleCount: 0,
      maxDepth: 0,
      fields: [],
      operators: [],
      combinators: [],
    };
  }

  const fields = new Set();
  const operators = new Set();
  const combinators = new Set();
  let ruleCount = 0;
  let maxDepth = 0;

  const visit = (node, depth = 0) => {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return;
    maxDepth = Math.max(maxDepth, depth);

    if (Array.isArray(node.rules)) {
      combinators.add(String(node.combinator || node.condition || 'and').toLowerCase());
      node.rules.forEach(child => visit(child, depth + 1));
      return;
    }

    ruleCount += 1;
    if (typeof node.field === 'string') fields.add(node.field);
    if (typeof node.operator === 'string') operators.add(node.operator);
  };

  visit(conditionAst);

  return {
    ruleCount,
    maxDepth,
    fields: Array.from(fields),
    operators: Array.from(operators),
    combinators: Array.from(combinators),
  };
};

const analyzeConditionAstText = (text) => {
  if (!text || !String(text).trim()) {
    return {
      status: 'empty',
      error: null,
      parsed: null,
      summary: summarizeConditionAst(null),
      isValid: true,
    };
  }

  try {
    const parsed = JSON.parse(text);
    const error = validateConditionAstValue(parsed);
    return {
      status: error ? 'invalid' : 'valid',
      error,
      parsed,
      summary: summarizeConditionAst(parsed),
      isValid: !error,
    };
  } catch (error) {
    return {
      status: 'invalid-json',
      error: `Quest condition AST contains invalid JSON: ${error.message}`,
      parsed: null,
      summary: summarizeConditionAst(null),
      isValid: false,
    };
  }
};

const analyzeConditionAstValue = (value) => {
  const error = validateConditionAstValue(value);
  return {
    status: value ? (error ? 'invalid' : 'valid') : 'empty',
    error,
    parsed: value,
    summary: summarizeConditionAst(value),
    isValid: !error,
  };
};

const validateSpotlightTargetValue = (spotlightTarget) => {
  if (spotlightTarget === null || spotlightTarget === undefined) return null;
  if (typeof spotlightTarget !== 'object' || Array.isArray(spotlightTarget)) {
    return 'Spotlight target must be a JSON object.';
  }

  if (spotlightTarget.position !== undefined) {
    if (typeof spotlightTarget.position !== 'string' || !SPOTLIGHT_POSITION_SET.has(spotlightTarget.position)) {
      return 'Spotlight target position must be one of: top, bottom, left, right.';
    }
  }

  return null;
};

const summarizeSpotlightTarget = (spotlightTarget) => {
  if (!spotlightTarget || typeof spotlightTarget !== 'object' || Array.isArray(spotlightTarget)) {
    return {
      keys: [],
      position: null,
      hasSelector: false,
      hasFallbackRect: false,
    };
  }

  return {
    keys: Object.keys(spotlightTarget),
    position: typeof spotlightTarget.position === 'string' ? spotlightTarget.position : null,
    hasSelector: typeof spotlightTarget.selector === 'string' && spotlightTarget.selector.trim().length > 0,
    hasFallbackRect: spotlightTarget.fallbackRect && typeof spotlightTarget.fallbackRect === 'object' && !Array.isArray(spotlightTarget.fallbackRect),
  };
};

const analyzeSpotlightTargetValue = (value) => {
  const error = validateSpotlightTargetValue(value);
  return {
    status: value ? (error ? 'invalid' : 'valid') : 'empty',
    error,
    parsed: value,
    summary: summarizeSpotlightTarget(value),
    isValid: !error,
  };
};

const splitSpotlightTargetValue = (spotlightTarget) => {
  if (!spotlightTarget || typeof spotlightTarget !== 'object' || Array.isArray(spotlightTarget)) {
    return {
      builderValues: { ...SPOTLIGHT_BUILDER_DEFAULTS },
      extraProperties: {},
      fallbackRectExtraProperties: {},
    };
  }

  const {
    selector,
    position,
    displayName,
    fallbackRect,
    ...extraProperties
  } = spotlightTarget;

  const fallbackRectValue = fallbackRect && typeof fallbackRect === 'object' && !Array.isArray(fallbackRect)
    ? fallbackRect
    : null;

  const {
    x,
    y,
    width,
    height,
    ...fallbackRectExtraProperties
  } = fallbackRectValue || {};

  return {
    builderValues: {
      selector: typeof selector === 'string' ? selector : undefined,
      position: typeof position === 'string' ? position : SPOTLIGHT_BUILDER_DEFAULTS.position,
      displayName: typeof displayName === 'string' ? displayName : '',
      useFallbackRect: Boolean(fallbackRectValue),
      fallbackX: typeof x === 'number' ? x : undefined,
      fallbackY: typeof y === 'number' ? y : undefined,
      fallbackWidth: typeof width === 'number' ? width : undefined,
      fallbackHeight: typeof height === 'number' ? height : undefined,
    },
    extraProperties,
    fallbackRectExtraProperties,
  };
};

const formatJson = (value, fallback = null) => JSON.stringify(value ?? fallback ?? {}, null, 2);

const parseOptionalJson = (text, label) => {
  if (!text || !text.trim()) return null;
  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error(`${label} contains invalid JSON: ${error.message}`);
  }
};

const getErrorMessage = (error, fallback) => (
  error?.response?.data?.message
  || error?.response?.data?.title
  || error?.message
  || fallback
);

const formatFileSize = (bytes) => {
  const numericBytes = Number(bytes) || 0;
  if (numericBytes >= 1_048_576) {
    return `${(numericBytes / 1_048_576).toFixed(1)} MB`;
  }
  if (numericBytes >= 1024) {
    return `${(numericBytes / 1024).toFixed(1)} KB`;
  }
  return `${numericBytes} B`;
};

const stripHtml = (value) => String(value || '')
  .replace(/<br\s*\/?>/gi, ' ')
  .replace(/<[^>]+>/g, ' ')
  .replace(/&nbsp;/gi, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const getJsonPreview = (value) => {
  if (!value) {
    return '';
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return '';
  }
};

const getLearningResourceKind = (resource) => {
  const contentType = String(resource?.contentType || '').toLowerCase();
  const extension = String(resource?.fileExtension || '').toLowerCase();

  if (contentType.startsWith('video/') || extension === '.mp4') {
    return {
      key: 'video',
      icon: VideoCameraOutlined,
      color: 'magenta',
      label: 'Video',
    };
  }

  return {
    key: 'pdf',
    icon: FilePdfOutlined,
    color: 'red',
    label: 'PDF',
  };
};

const isSupportedLearningResourceFile = (file) => {
  const name = String(file?.name || '').toLowerCase();
  const type = String(file?.type || '').toLowerCase();

  return (
    name.endsWith('.pdf')
    || name.endsWith('.mp4')
    || (type === 'application/pdf' && name.endsWith('.pdf'))
    || (type === 'video/mp4' && name.endsWith('.mp4'))
  );
};

/**
 * Editor for creating and maintaining a training profile.
 * @returns {JSX.Element} Profile editor page.
 */
const TrainingProfileEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isNew = !id;
  const profileId = id ? Number(id) : null;

  const [profileForm] = Form.useForm();
  const [questForm] = Form.useForm();
  const [stepForm] = Form.useForm();
  const [spotlightBuilderForm] = Form.useForm();
  const conditionAstText = Form.useWatch('conditionAstText', questForm);
  const spotlightBuilderSelector = Form.useWatch('selector', spotlightBuilderForm);
  const spotlightBuilderUseFallbackRect = Form.useWatch('useFallbackRect', spotlightBuilderForm);

  const [profile, setProfile] = useState(null);
  const [countries, setCountries] = useState([]);
  const [quizCollections, setQuizCollections] = useState([]);
  const [quizAssignmentIds, setQuizAssignmentIds] = useState([]);
  const [quizSearchText, setQuizSearchText] = useState('');
  const [configurationDraft, setConfigurationDraft] = useState(() => createTrainingProfileConfigurationDraft());
  const [resourceUploadLoading, setResourceUploadLoading] = useState(false);
  const [resourceDeleteLoadingId, setResourceDeleteLoadingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [ownershipModalOpen, setOwnershipModalOpen] = useState(false);
  const [bulkImportModalOpen, setBulkImportModalOpen] = useState(false);
  const [questModalOpen, setQuestModalOpen] = useState(false);
  const [editingQuest, setEditingQuest] = useState(null);
  const [stepModalOpen, setStepModalOpen] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [stepParentQuest, setStepParentQuest] = useState(null);
  const [spotlightBuilderOpen, setSpotlightBuilderOpen] = useState(false);
  const [spotlightBuilderStepContext, setSpotlightBuilderStepContext] = useState(null);
  const [spotlightBuilderExtraProperties, setSpotlightBuilderExtraProperties] = useState({});
  const [spotlightBuilderFallbackRectExtraProperties, setSpotlightBuilderFallbackRectExtraProperties] = useState({});
  const [conditionAstHelpModalState, setConditionAstHelpModalState] = useState({
    open: false,
    title: '',
    analysis: analyzeConditionAstValue(null),
  });
  const resourceInputRef = useRef(null);
  const conditionAstDraftAnalysis = useMemo(
    () => analyzeConditionAstText(conditionAstText),
    [conditionAstText]
  );

  const ownership = useOwnership('TrainingProfile', profileId, {
    autoLoad: !!profileId,
    onShareSuccess: () => message.success(t('ownership.shareSuccess', 'Access shared successfully')),
    onRevokeSuccess: () => message.success(t('ownership.revokeSuccess', 'Access revoked successfully')),
  });

  const countryOptions = useMemo(() => countries.map(country => ({
    value: country.id,
    label: `${country.language}${country.name ? ` · ${country.name}` : ''}`,
  })), [countries]);

  const questIconOptions = useMemo(() => {
    const baseOptions = AVAILABLE_QUEST_ICONS.map((icon) => ({
      value: icon.key,
      label: (
        <span className={styles.iconSelectOption}>
          <span className={styles.iconSelectPreview}>{renderQuestIcon(icon.key)}</span>
          <span>{icon.label}</span>
        </span>
      ),
      searchLabel: `${icon.label} ${icon.key}`.toLowerCase(),
    }));

    const currentIconKey = editingQuest?.iconKey;
    if (currentIconKey && !AVAILABLE_QUEST_ICONS.some((icon) => icon.key === currentIconKey)) {
      return [
        {
          value: currentIconKey,
          label: (
            <span className={styles.iconSelectOption}>
              <span className={styles.iconSelectPreview}>{currentIconKey}</span>
              <span>{`${currentIconKey} (${t('trainingProfiles.currentValue', 'current value')})`}</span>
            </span>
          ),
          searchLabel: currentIconKey.toLowerCase(),
        },
        ...baseOptions,
      ];
    }

    return baseOptions;
  }, [editingQuest?.iconKey, t]);

  const spotlightSelectorOptions = useMemo(
    () => AVAILABLE_SPOTLIGHT_SELECTORS
      .map((entry) => ({
        categoryLabel: entry.category || t('trainingProfiles.spotlightTargetDefaultCategory', 'Simulation target'),
        value: entry.selector,
        name: entry.name,
        category: entry.category,
        description: entry.description,
        imageSrc: entry.imageSrc,
        imageAlt: entry.imageAlt,
        label: (
          <span className={styles.spotlightBuilderSelectorOptionLabel}>
            {entry.imageSrc && (
              <span className={styles.spotlightBuilderSelectorOptionPreview}>
                <img src={entry.imageSrc} alt={entry.imageAlt || entry.name} />
              </span>
            )}
            <span className={styles.spotlightBuilderSelectorOptionText}>
              <span className={styles.spotlightBuilderSelectorOptionMeta}>{entry.category || t('trainingProfiles.spotlightTargetDefaultCategory', 'Simulation target')}</span>
              <strong>{entry.name}</strong>
              <code>{entry.selector}</code>
            </span>
          </span>
        ),
        searchLabel: `${entry.category || t('trainingProfiles.spotlightTargetDefaultCategory', 'Simulation target')} ${entry.name} ${entry.selector}`.toLowerCase(),
      })),
    [t]
  );

  const spotlightBuilderSelectedOption = useMemo(
    () => spotlightSelectorOptions.find((entry) => entry.value === spotlightBuilderSelector) || null,
    [spotlightBuilderSelector, spotlightSelectorOptions]
  );

  const quizCollectionMap = useMemo(
    () => new Map(quizCollections.map(collection => [collection.id, collection])),
    [quizCollections]
  );

  const filteredQuizCollections = useMemo(() => {
    const query = quizSearchText.trim().toLowerCase();
    if (!query) {
      return quizCollections;
    }

    return quizCollections.filter((collection) => {
      const haystack = [
        collection.name,
        collection.ownerName,
        collection.addedByName,
      ].filter(Boolean).join(' ').toLowerCase();

      return haystack.includes(query);
    });
  }, [quizCollections, quizSearchText]);

  const selectedQuizCollections = useMemo(
    () => quizAssignmentIds.map((quizCollectionId) => ({
      quizCollectionId,
      collection: quizCollectionMap.get(quizCollectionId) || null,
    })),
    [quizAssignmentIds, quizCollectionMap]
  );

  const learningResources = useMemo(
    () => Array.isArray(profile?.learningResources) ? profile.learningResources : [],
    [profile]
  );

  const loadReferenceData = useCallback(async () => {
    setReferenceLoading(true);
    try {
      const [countryData, collectionData] = await Promise.all([
        countriesService.getAllCountries({ suppressNotification: true }),
        trainingProfilesService.getTrainingProfileAvailableQuizCollections({ suppressNotification: true }),
      ]);
      setCountries(Array.isArray(countryData) ? countryData : []);
      setQuizCollections(Array.isArray(collectionData) ? collectionData : []);
    } catch (error) {
      message.warning(t('trainingProfiles.referenceLoadWarning', 'Some editor dropdown data could not be loaded.'));
    } finally {
      setReferenceLoading(false);
    }
  }, [t]);

  const applyProfileToForm = useCallback((data) => {
    setProfile(data);
    profileForm.setFieldsValue({
      name: data.name,
      countryId: data.countryId || undefined,
      additionalTextRichContent: data.additionalTextRichContent || '',
    });
    setConfigurationDraft(createTrainingProfileConfigurationDraft({
      uiConfiguration: data.uiConfiguration,
      simulationDefaults: data.simulationDefaults,
      fixedValues: data.fixedValues,
    }));
    setQuizAssignmentIds((data.quizAssignments || []).map(assignment => assignment.quizCollectionId));
  }, [profileForm]);

  const loadProfile = useCallback(async () => {
    if (!profileId) return;
    setLoading(true);
    try {
      const data = await trainingProfilesService.getTrainingProfile(profileId, { suppressNotification: true });
      applyProfileToForm(data);
    } catch (error) {
      message.error(getErrorMessage(error, t('trainingProfiles.loadError', 'Failed to load training profile')));
    } finally {
      setLoading(false);
    }
  }, [applyProfileToForm, profileId, t]);

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  useEffect(() => {
    if (isNew) {
      profileForm.setFieldsValue({
        name: '',
        countryId: undefined,
        additionalTextRichContent: '',
      });
      setConfigurationDraft(createTrainingProfileConfigurationDraft());
      return;
    }
    loadProfile();
  }, [isNew, loadProfile, profileForm]);

  const buildProfilePayload = async () => {
    const values = await profileForm.validateFields();
    const uiConfiguration = buildTrainingProfileUiConfiguration(configurationDraft);
    const simulationDefaults = buildTrainingProfileSimulationDefaults(configurationDraft);
    const fixedValues = buildTrainingProfileFixedValues(configurationDraft);

    if (isNew) {
      return {
        name: values.name,
        countryId: values.countryId,
        additionalTextRichContent: values.additionalTextRichContent || null,
        uiConfiguration,
        simulationDefaults,
        fixedValues,
      };
    }

    return {
      name: values.name,
      countryId: values.countryId,
      clearCountry: false,
      additionalTextRichContent: values.additionalTextRichContent || null,
      clearAdditionalText: !values.additionalTextRichContent,
      uiConfiguration,
      clearUiConfiguration: !uiConfiguration,
      simulationDefaults,
      clearSimulationDefaults: !simulationDefaults,
      fixedValues,
      clearFixedValues: !fixedValues,
    };
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const payload = await buildProfilePayload();
      if (isNew) {
        const created = await trainingProfilesService.createTrainingProfile(payload);
        message.success(t('trainingProfiles.createSuccess', 'Training profile created'));
        navigate(`/training-profiles/${created.id}`, { replace: true });
      } else {
        const updated = await trainingProfilesService.updateTrainingProfile(profileId, payload);
        message.success(t('trainingProfiles.updateSuccess', 'Training profile saved'));
        if (updated) applyProfileToForm(updated);
        await loadProfile();
      }
    } catch (error) {
      message.error(getErrorMessage(error, t('trainingProfiles.saveError', 'Failed to save training profile')));
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async () => {
    if (!profileId || !profile) return;
    setSaving(true);
    try {
      if (profile.isPublished) {
        await trainingProfilesService.unpublishTrainingProfile(profileId);
        message.success(t('trainingProfiles.unpublishSuccess', 'Training profile unpublished'));
      } else {
        await trainingProfilesService.publishTrainingProfile(profileId);
        message.success(t('trainingProfiles.publishSuccess', 'Training profile published'));
      }
      await loadProfile();
    } catch (error) {
      message.error(getErrorMessage(error, t('trainingProfiles.publishError', 'Failed to update publish status')));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveQuizAssignments = async () => {
    if (!profileId) {
      message.info(t('trainingProfiles.saveBeforeQuizAssignments', 'Save the profile before assigning quizzes.'));
      return;
    }
    setSaving(true);
    try {
      await trainingProfilesService.updateTrainingProfileQuizAssignments(
        profileId,
        quizAssignmentIds.map((quizCollectionId, index) => ({
          quizCollectionId,
          orderIndex: index,
          displayLabel: null,
        }))
      );
      message.success(t('trainingProfiles.quizAssignmentsSaved', 'Quiz assignments saved'));
      await loadProfile();
    } catch (error) {
      message.error(getErrorMessage(error, t('trainingProfiles.quizAssignmentsError', 'Failed to save quiz assignments')));
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLearnerLink = () => {
    if (!profileId) return;
    const url = `${window.location.origin}/simulations/heat-pump-training?profileId=${profileId}`;
    navigator.clipboard.writeText(url);
    message.success(t('trainingProfiles.linkCopied', 'Training profile link copied'));
  };

  const handlePickLearningResources = useCallback(() => {
    resourceInputRef.current?.click();
  }, []);

  const handleLearningResourcesSelected = useCallback(async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';

    if (!profileId) {
      message.info(t('trainingProfiles.saveFirst', 'Save the profile first.'));
      return;
    }

    if (files.length === 0) {
      return;
    }

    const invalidFile = files.find(file => !isSupportedLearningResourceFile(file));
    if (invalidFile) {
      message.error(t('trainingProfiles.learningResourcesInvalidFile', 'Only PDF and MP4 files are allowed for learning resources.'));
      return;
    }

    setResourceUploadLoading(true);
    try {
      for (const file of files) {
        await trainingProfilesService.uploadTrainingProfileLearningResource(profileId, file, {}, null, { suppressNotification: true });
      }

      message.success(
        files.length === 1
          ? t('trainingProfiles.learningResourceUploadSuccess', 'Learning resource uploaded')
          : t('trainingProfiles.learningResourcesUploadSuccess', 'Learning resources uploaded')
      );
      await loadProfile();
    } catch (error) {
      message.error(getErrorMessage(error, t('trainingProfiles.learningResourceUploadError', 'Failed to upload learning resource')));
    } finally {
      setResourceUploadLoading(false);
    }
  }, [loadProfile, profileId, t]);

  const handleDeleteLearningResource = useCallback(async (resourceId) => {
    if (!profileId) {
      return;
    }

    setResourceDeleteLoadingId(resourceId);
    try {
      await trainingProfilesService.deleteTrainingProfileLearningResource(profileId, resourceId, { suppressNotification: true });
      message.success(t('trainingProfiles.learningResourceDeleteSuccess', 'Learning resource removed'));
      await loadProfile();
    } catch (error) {
      message.error(getErrorMessage(error, t('trainingProfiles.learningResourceDeleteError', 'Failed to remove learning resource')));
    } finally {
      setResourceDeleteLoadingId(null);
    }
  }, [loadProfile, profileId, t]);

  const openLearningResource = useCallback((resource) => {
    const resourceUrl = resource?.relativeUrl || resource?.downloadUrl;
    if (!resourceUrl) {
      return;
    }

    window.open(resourceUrl, '_blank', 'noopener,noreferrer');
  }, []);

  const handleOpenQuizCollection = useCallback((quizCollectionId) => {
    window.open(`${window.location.origin}/quiz-collections/${quizCollectionId}`, '_blank', 'noopener,noreferrer');
  }, []);

  const handleAddQuizAssignment = useCallback((quizCollectionId) => {
    setQuizAssignmentIds((prev) => (prev.includes(quizCollectionId) ? prev : [...prev, quizCollectionId]));
  }, []);

  const handleRemoveQuizAssignment = useCallback((quizCollectionId) => {
    setQuizAssignmentIds((prev) => prev.filter((id) => id !== quizCollectionId));
  }, []);

  const handleMoveQuizAssignment = useCallback((quizCollectionId, direction) => {
    setQuizAssignmentIds((prev) => {
      const currentIndex = prev.indexOf(quizCollectionId);
      if (currentIndex === -1) {
        return prev;
      }

      const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (nextIndex < 0 || nextIndex >= prev.length) {
        return prev;
      }

      const next = [...prev];
      [next[currentIndex], next[nextIndex]] = [next[nextIndex], next[currentIndex]];
      return next;
    });
  }, []);

  const handleConfigurationChange = useCallback((itemId, patch) => {
    setConfigurationDraft(prev => {
      const nextState = {
        ...prev[itemId],
        ...patch,
      };

      if (nextState.visible === false) {
        nextState.editable = false;
      }

      if (nextState.fixedEnabled) {
        nextState.editable = false;
        nextState.defaultEnabled = false;
      }

      return {
        ...prev,
        [itemId]: nextState,
      };
    });
  }, []);

  const renderConfigurationValueInput = useCallback((item, value, onChange, disabled = false) => {
    if (!item.editor) {
      return null;
    }

    if (item.editor.type === 'boolean') {
      return <Switch checked={Boolean(value)} onChange={onChange} disabled={disabled} />;
    }

    if (item.editor.type === 'select') {
      return (
        <Select
          disabled={disabled}
          options={item.editor.options.map(option => ({
            ...option,
            label: option.labelKey ? t(option.labelKey, option.label) : option.label,
          }))}
          value={value}
          style={{ width: '100%' }}
          onChange={onChange}
        />
      );
    }

    return (
      <div className={styles.configValueInputWrap}>
        <InputNumber
          disabled={disabled}
          min={item.editor.min}
          max={item.editor.max}
          step={item.editor.step}
          value={value}
          style={{ width: '100%' }}
          onChange={nextValue => onChange(nextValue)}
        />
        {item.editor.unit && <span className={styles.configValueUnit}>{item.editor.unit}</span>}
      </div>
    );
  }, [t]);

  const validateConditionAstField = useCallback(async (_, value) => {
    const analysis = analyzeConditionAstText(value);
    if (analysis.error) {
      throw new Error(analysis.error);
    }
  }, []);

  const handleUseStarterConditionAst = useCallback(() => {
    questForm.setFieldsValue({ conditionAstText: formatJson(DEFAULT_CONDITION) });
  }, [questForm]);

  const handleUseCrossFieldConditionAst = useCallback(() => {
    questForm.setFieldsValue({ conditionAstText: formatJson(CROSS_FIELD_CONDITION) });
  }, [questForm]);

  const handleUseTwoConditionAst = useCallback(() => {
    questForm.setFieldsValue({ conditionAstText: formatJson(TWO_CONDITION_EXAMPLE) });
  }, [questForm]);

  const handleFormatConditionAst = useCallback(() => {
    const value = questForm.getFieldValue('conditionAstText');

    if (!value || !String(value).trim()) {
      questForm.setFieldsValue({ conditionAstText: formatJson(DEFAULT_CONDITION) });
      return;
    }

    try {
      const parsed = JSON.parse(value);
      questForm.setFieldsValue({ conditionAstText: formatJson(parsed) });
      message.success(t('trainingProfiles.conditionAstFormatted', 'Condition AST JSON formatted'));
    } catch (error) {
      message.error(t('trainingProfiles.conditionAstFormatError', 'Fix the JSON syntax errors before formatting this condition AST.'));
    }
  }, [questForm, t]);

  const handleSelectSpotlightBuilderSelector = useCallback((selector) => {
    const selectedOption = spotlightSelectorOptions.find((entry) => entry.value === selector) || null;
    const currentSelector = spotlightBuilderForm.getFieldValue('selector');
    const currentDisplayName = spotlightBuilderForm.getFieldValue('displayName');
    const currentOption = spotlightSelectorOptions.find((entry) => entry.value === currentSelector) || null;
    const nextValues = { selector };

    if (!currentDisplayName || (currentOption && currentDisplayName === currentOption.name)) {
      nextValues.displayName = selectedOption?.name || '';
    }

    spotlightBuilderForm.setFieldsValue(nextValues);
  }, [spotlightBuilderForm, spotlightSelectorOptions]);

  const handleOpenStepSpotlightBuilder = useCallback((quest, step) => {
    const { builderValues, extraProperties, fallbackRectExtraProperties } = splitSpotlightTargetValue(step?.spotlightTarget);
    spotlightBuilderForm.resetFields();
    spotlightBuilderForm.setFieldsValue(builderValues);
    setSpotlightBuilderExtraProperties(extraProperties);
    setSpotlightBuilderFallbackRectExtraProperties(fallbackRectExtraProperties);
    setSpotlightBuilderStepContext({ quest, step });
    setSpotlightBuilderOpen(true);
  }, [spotlightBuilderForm]);

  const handleCloseSpotlightBuilder = useCallback(() => {
    setSpotlightBuilderOpen(false);
    setSpotlightBuilderStepContext(null);
  }, []);

  const handleApplySpotlightBuilder = useCallback(async () => {
    if (!spotlightBuilderStepContext?.step) {
      return;
    }

    const values = await spotlightBuilderForm.validateFields();

    const hasAnyValue = Boolean(
      values.selector
      || values.displayName
      || values.useFallbackRect
      || values.position
    );

    let nextSpotlightTarget = null;

    if (!hasAnyValue || (!values.selector && !values.useFallbackRect)) {
      nextSpotlightTarget = null;
    } else {
      nextSpotlightTarget = {
        ...spotlightBuilderExtraProperties,
      };

      if (values.selector) {
        nextSpotlightTarget.selector = values.selector;
      }

      if (values.position) {
        nextSpotlightTarget.position = values.position;
      }

      if (values.displayName) {
        nextSpotlightTarget.displayName = values.displayName.trim();
      }

      if (values.useFallbackRect) {
        nextSpotlightTarget.fallbackRect = {
          ...spotlightBuilderFallbackRectExtraProperties,
          x: Number(values.fallbackX),
          y: Number(values.fallbackY),
          width: Number(values.fallbackWidth),
          height: Number(values.fallbackHeight),
        };
      }
    }

    const sourceStep = spotlightBuilderStepContext.step;
    const payload = {
      orderIndex: sourceStep.orderIndex ?? 0,
      stepKey: sourceStep.stepKey || null,
      taskTitle: sourceStep.taskTitle,
      instructionRichContent: sourceStep.instructionRichContent || null,
      detailedGuidanceRichContent: sourceStep.detailedGuidanceRichContent || null,
      successIndicatorRichContent: sourceStep.successIndicatorRichContent || null,
      spotlightTarget: nextSpotlightTarget,
    };

    setSaving(true);
    try {
      await trainingProfilesService.updateTrainingProfileStep(sourceStep.id, payload);
      message.success(t('trainingProfiles.stepUpdated', 'Tutorial step updated'));
      setSpotlightBuilderOpen(false);
      setSpotlightBuilderStepContext(null);
      await loadProfile();
    } catch (error) {
      message.error(getErrorMessage(error, t('trainingProfiles.stepSaveError', 'Failed to save tutorial step')));
    } finally {
      setSaving(false);
    }
  }, [loadProfile, spotlightBuilderFallbackRectExtraProperties, spotlightBuilderExtraProperties, spotlightBuilderForm, spotlightBuilderStepContext, t]);

  const handleOpenConditionAstHelpModal = useCallback((title, value) => {
    setConditionAstHelpModalState({
      open: true,
      title,
      analysis: analyzeConditionAstValue(value),
    });
  }, []);

  const handleCloseConditionAstHelpModal = useCallback(() => {
    setConditionAstHelpModalState((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const openQuestModal = useCallback((quest = null) => {
    setEditingQuest(quest);
    questForm.resetFields();
    questForm.setFieldsValue({
      orderIndex: quest?.orderIndex ?? (profile?.tutorialQuests?.length || 0),
      title: quest?.title || '',
      category: quest?.category || '',
      difficulty: quest?.difficulty || '',
      estimatedTime: quest?.estimatedTime || '',
      iconKey: quest?.iconKey || '',
      isChallenge: !!quest?.isChallenge,
      learningObjectivesRichContent: quest?.learningObjectivesRichContent || '',
      scenarioRichContent: quest?.scenarioRichContent || '',
      realWorldConnectionRichContent: quest?.realWorldConnectionRichContent || '',
      completionDescriptionRichContent: quest?.completionDescriptionRichContent || '',
      knowledgeSynthesisRichContent: quest?.knowledgeSynthesisRichContent || '',
      practicalTipRichContent: quest?.practicalTipRichContent || '',
      conditionAstText: quest?.conditionAst ? formatJson(quest.conditionAst) : formatJson(DEFAULT_CONDITION),
    });
    setQuestModalOpen(true);
  }, [profile?.tutorialQuests?.length, questForm]);

  const handleSaveQuest = async () => {
    if (!profileId) {
      message.info(t('trainingProfiles.saveFirst', 'Save the profile first.'));
      return;
    }
    try {
      const values = await questForm.validateFields();
      const conditionAst = parseOptionalJson(values.conditionAstText, 'Quest condition AST');
      const payload = {
        orderIndex: values.orderIndex ?? 0,
        title: values.title,
        category: values.category || null,
        difficulty: values.difficulty || null,
        estimatedTime: values.estimatedTime || null,
        iconKey: values.iconKey || null,
        isChallenge: !!values.isChallenge,
        learningObjectivesRichContent: values.learningObjectivesRichContent || null,
        scenarioRichContent: values.scenarioRichContent || null,
        realWorldConnectionRichContent: values.realWorldConnectionRichContent || null,
        completionDescriptionRichContent: values.completionDescriptionRichContent || null,
        knowledgeSynthesisRichContent: values.knowledgeSynthesisRichContent || null,
        practicalTipRichContent: values.practicalTipRichContent || null,
        conditionAst,
      };

      setSaving(true);
      if (editingQuest) {
        await trainingProfilesService.updateTrainingProfileQuest(editingQuest.id, payload);
        message.success(t('trainingProfiles.questUpdated', 'Tutorial quest updated'));
      } else {
        await trainingProfilesService.addTrainingProfileQuest(profileId, payload);
        message.success(t('trainingProfiles.questAdded', 'Tutorial quest added'));
      }
      setQuestModalOpen(false);
      await loadProfile();
    } catch (error) {
      message.error(getErrorMessage(error, t('trainingProfiles.questSaveError', 'Failed to save tutorial quest')));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuest = useCallback(async (quest) => {
    setSaving(true);
    try {
      await trainingProfilesService.deleteTrainingProfileQuest(quest.id);
      message.success(t('trainingProfiles.questDeleted', 'Tutorial quest deleted'));
      await loadProfile();
    } catch (error) {
      message.error(getErrorMessage(error, t('trainingProfiles.questDeleteError', 'Failed to delete tutorial quest')));
    } finally {
      setSaving(false);
    }
  }, [loadProfile, t]);

  const openStepModal = useCallback((quest, step = null) => {
    setStepParentQuest(quest);
    setEditingStep(step);
    stepForm.resetFields();
    stepForm.setFieldsValue({
      orderIndex: step?.orderIndex ?? (quest?.steps?.length || 0),
      stepKey: step?.stepKey || '',
      taskTitle: step?.taskTitle || '',
      instructionRichContent: step?.instructionRichContent || '',
      detailedGuidanceRichContent: step?.detailedGuidanceRichContent || '',
      successIndicatorRichContent: step?.successIndicatorRichContent || '',
      spotlightTargetText: step?.spotlightTarget ? formatJson(step.spotlightTarget) : '',
    });
    setStepModalOpen(true);
  }, [stepForm]);

  const handleSaveStep = async () => {
    if (!stepParentQuest) return;
    try {
      const values = await stepForm.validateFields();
      const spotlightTarget = parseOptionalJson(values.spotlightTargetText, 'Spotlight target');
      const payload = {
        orderIndex: values.orderIndex ?? 0,
        stepKey: values.stepKey || null,
        taskTitle: values.taskTitle,
        instructionRichContent: values.instructionRichContent || null,
        detailedGuidanceRichContent: values.detailedGuidanceRichContent || null,
        successIndicatorRichContent: values.successIndicatorRichContent || null,
        spotlightTarget,
      };

      setSaving(true);
      if (editingStep) {
        await trainingProfilesService.updateTrainingProfileStep(editingStep.id, payload);
        message.success(t('trainingProfiles.stepUpdated', 'Tutorial step updated'));
      } else {
        await trainingProfilesService.addTrainingProfileStep(stepParentQuest.id, payload);
        message.success(t('trainingProfiles.stepAdded', 'Tutorial step added'));
      }
      setStepModalOpen(false);
      await loadProfile();
    } catch (error) {
      message.error(getErrorMessage(error, t('trainingProfiles.stepSaveError', 'Failed to save tutorial step')));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStep = useCallback(async (step) => {
    setSaving(true);
    try {
      await trainingProfilesService.deleteTrainingProfileStep(step.id);
      message.success(t('trainingProfiles.stepDeleted', 'Tutorial step deleted'));
      await loadProfile();
    } catch (error) {
      message.error(getErrorMessage(error, t('trainingProfiles.stepDeleteError', 'Failed to delete tutorial step')));
    } finally {
      setSaving(false);
    }
  }, [loadProfile, t]);

  const renderMetaCard = useCallback((label, value) => {
    const isEmpty = value === null || value === undefined || value === '';
    return (
      <div className={`${styles.tutorialMetaCard}${isEmpty ? ` ${styles.tutorialMetaCardMuted}` : ''}`}>
        <span className={styles.tutorialMetaLabel}>{label}</span>
        <span className={styles.tutorialMetaValue}>
          {isEmpty ? t('trainingProfiles.notFilled', 'Not filled') : value}
        </span>
      </div>
    );
  }, [t]);

  const renderContentCard = useCallback((label, value) => {
    const isFilled = value && stripHtml(value);
    
    return (
      <div className={`${styles.tutorialContentCard}${!isFilled ? ` ${styles.tutorialContentCardEmpty}` : ''}`}>
        <div className={styles.tutorialContentCardHeader}>
          <div className={styles.tutorialContentLabel}>{label}</div>
        </div>
        {isFilled ? (
          <div className={styles.tutorialContentHtml} dangerouslySetInnerHTML={{ __html: value }} />
        ) : (
          <div className={`${styles.tutorialContentPreview} ${styles.tutorialContentPreviewEmpty}`}>
            {t('trainingProfiles.notFilledHelp', 'No content has been added yet.')}
          </div>
        )}
      </div>
    );
  }, [t]);

  const renderValidationSuccess = useCallback((message, detail, options = {}) => {
    const compact = options.compact === true;

    return (
      <div className={`${styles.validationInlineStatus}${compact ? ` ${styles.validationInlineStatusCompact}` : ''}`}>
        <CheckCircleOutlined className={styles.validationInlineStatusIcon} />
        <span className={styles.validationInlineStatusMessage}>{message}</span>
        {detail && (
          <>
            <span className={styles.validationInlineStatusDivider}>·</span>
            <span className={styles.validationInlineStatusDetail}>{detail}</span>
          </>
        )}
      </div>
    );
  }, []);

  const renderConditionAstStatus = useCallback((analysis, options = {}) => {
    const compact = options.compact === true;

    if (analysis.status === 'empty') {
      return (
        <Alert
          showIcon
          type="info"
          className={`${styles.conditionAstStatus}${compact ? ` ${styles.conditionAstStatusCompact}` : ''}`}
          message={t('trainingProfiles.conditionAstOptional', 'Condition AST is optional')}
          description={t('trainingProfiles.conditionAstOptionalHelp', 'Leave it empty if this quest should not depend on simulator state. Add a valid AST when quest completion should react to live simulation metrics.')}
        />
      );
    }

    if (analysis.isValid) {
      return renderValidationSuccess(
        t('trainingProfiles.conditionAstValid', 'Valid condition'),
        `${analysis.summary.ruleCount} ${t('trainingProfiles.rulesLabel', 'rules')} · ${t('trainingProfiles.maxDepthShortLabel', 'depth')} ${analysis.summary.maxDepth}`,
        { compact }
      );
    }

    return (
      <Alert
        showIcon
        type="error"
        className={`${styles.conditionAstStatus}${compact ? ` ${styles.conditionAstStatusCompact}` : ''}`}
        message={t('trainingProfiles.conditionAstInvalid', 'Condition AST is invalid')}
        description={analysis.error}
      />
    );
  }, [renderValidationSuccess, t]);

  const renderConditionAstReference = useCallback((analysis, options = {}) => {
    const compact = options.compact === true;
    const showExamples = options.showExamples !== false;
    const fieldsUsed = analysis.summary.fields.map(field => CONDITION_FIELD_MAP[field]?.label || field);

    return (
      <div className={`${styles.conditionAstReference}${compact ? ` ${styles.conditionAstReferenceCompact}` : ''}`}>
        {analysis.summary.ruleCount > 0 && (
          <div className={styles.conditionAstSummaryRow}>
            <div className={styles.conditionAstSummaryItem}>
              <span>{t('trainingProfiles.rulesLabel', 'Rules')}</span>
              <strong>{analysis.summary.ruleCount}</strong>
            </div>
            <div className={styles.conditionAstSummaryItem}>
              <span>{t('trainingProfiles.maxDepthLabel', 'Max depth')}</span>
              <strong>{analysis.summary.maxDepth}</strong>
            </div>
            <div className={styles.conditionAstSummaryItemWide}>
              <span>{t('trainingProfiles.fieldsUsedLabel', 'Fields used')}</span>
              <strong>{fieldsUsed.length > 0 ? fieldsUsed.join(', ') : t('trainingProfiles.noneLabel', 'None')}</strong>
            </div>
          </div>
        )}

        <div className={styles.conditionAstReferenceGrid}>
          <div className={styles.conditionAstReferenceCard}>
            <div className={styles.conditionAstReferenceTitle}><CodeOutlined /> {t('trainingProfiles.conditionAstShapeTitle', 'Required shape')}</div>
            <div className={styles.conditionAstReferenceBody}>
              <div>{t('trainingProfiles.conditionAstGroupShape', 'Group node: { combinator, rules: [] }')}</div>
              <div>{t('trainingProfiles.conditionAstRuleShape', 'Rule node: { field, operator, value }')}</div>
              <div>{t('trainingProfiles.conditionAstMultiplierShape', 'Field-multiplier rule: use compareField and multiplier instead of value.')}</div>
              <div>{t('trainingProfiles.conditionAstLabelShape', 'Optional label: add label when you want learner-facing completion text.')}</div>
            </div>
          </div>

          <div className={styles.conditionAstReferenceCard}>
            <div className={styles.conditionAstReferenceTitle}><ApartmentOutlined /> {t('trainingProfiles.conditionAstCombinatorsTitle', 'Combinators')}</div>
            <div className={styles.conditionAstKeyList}>
              {CONDITION_COMBINATOR_DEFINITIONS.map(combinator => (
                <div key={combinator.key} className={styles.conditionAstKeyRow}>
                  <code>{combinator.key}</code>
                  <span>{combinator.description}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.conditionAstReferenceCard}>
            <div className={styles.conditionAstReferenceTitle}><CodeOutlined /> {t('trainingProfiles.conditionAstOperatorsTitle', 'Operators')}</div>
            <div className={styles.conditionAstKeyList}>
              {CONDITION_OPERATOR_DEFINITIONS.map(operator => (
                <div key={operator.key} className={styles.conditionAstKeyRow}>
                  <code>{operator.key}</code>
                  {!compact && <span>{operator.example}</span>}
                </div>
              ))}
            </div>
          </div>

          <div className={`${styles.conditionAstReferenceCard} ${styles.conditionAstReferenceCardWide}`}>
            <div className={styles.conditionAstReferenceTitle}><DatabaseOutlined /> {t('trainingProfiles.conditionAstFieldsTitle', 'Available fields')}</div>
            <div className={styles.conditionAstFieldGrid}>
              {CONDITION_FIELD_DEFINITIONS.map(field => (
                <div key={field.key} className={styles.conditionAstFieldRow}>
                  <code>{field.key}</code>
                  <span>{field.label}</span>
                  <small>{field.type}</small>
                </div>
              ))}
            </div>
          </div>
        </div>

        {showExamples && (
          <div className={styles.conditionAstExampleGrid}>
            <div className={styles.conditionAstReferenceCard}>
              <div className={styles.conditionAstReferenceTitle}><InfoCircleOutlined /> {t('trainingProfiles.conditionAstStarterExampleTitle', 'Starter example')}</div>
              <pre className={styles.conditionAstExampleCode}>{formatJson(DEFAULT_CONDITION)}</pre>
            </div>
            <div className={styles.conditionAstReferenceCard}>
              <div className={styles.conditionAstReferenceTitle}><InfoCircleOutlined /> {t('trainingProfiles.conditionAstTwoRuleExampleTitle', 'Two-rule example')}</div>
              <pre className={styles.conditionAstExampleCode}>{formatJson(TWO_CONDITION_EXAMPLE)}</pre>
            </div>
            <div className={styles.conditionAstReferenceCard}>
              <div className={styles.conditionAstReferenceTitle}><InfoCircleOutlined /> {t('trainingProfiles.conditionAstCrossFieldExampleTitle', 'Cross-field example')}</div>
              <pre className={styles.conditionAstExampleCode}>{formatJson(CROSS_FIELD_CONDITION)}</pre>
            </div>
          </div>
        )}
      </div>
    );
  }, [t]);

  const renderConditionAstCard = useCallback((value, helpTitle) => {
    const analysis = analyzeConditionAstValue(value);
    const preview = getJsonPreview(value);
    const isFilled = Boolean(preview);

    return (
      <div className={`${styles.tutorialContentCard} ${styles.tutorialContentCardWide}${!isFilled ? ` ${styles.tutorialContentCardEmpty}` : ''}`}>
        <div className={styles.tutorialContentCardHeader}>
          <div className={styles.tutorialContentLabel}>{t('trainingProfiles.conditionAst', 'Condition AST JSON')}</div>
        </div>
        {renderConditionAstStatus(analysis, { compact: true })}
        {isFilled ? (
          <pre className={styles.tutorialJsonPreview}>{preview}</pre>
        ) : (
          <p className={`${styles.tutorialContentPreview} ${styles.tutorialContentPreviewEmpty}`}>
            {t('trainingProfiles.noConditionAst', 'No condition AST has been configured for this quest yet.')}
          </p>
        )}
        <div className={styles.conditionAstHelpActions}>
          <Button
            size="small"
            icon={<QuestionCircleOutlined />}
            onClick={() => handleOpenConditionAstHelpModal(helpTitle || t('trainingProfiles.conditionAstGuide', 'Condition AST guide'), value)}
          >
            {t('trainingProfiles.openConditionAstGuide', 'Open Condition AST guide')}
          </Button>
        </div>
      </div>
    );
  }, [handleOpenConditionAstHelpModal, renderConditionAstStatus, t]);

  const renderSpotlightTargetStatus = useCallback((analysis, options = {}) => {
    const compact = options.compact === true;

    if (analysis.status === 'empty') {
      return (
        <div className={`${styles.inlineFieldNote}${compact ? ` ${styles.inlineFieldNoteCompact}` : ''}`}>
          {t('trainingProfiles.spotlightTargetOptionalSimple', 'Optional. Leave it empty if this step does not need a spotlight target.')}
        </div>
      );
    }

    if (analysis.isValid) {
      const detailParts = [];
      if (analysis.summary.position) {
        detailParts.push(`${t('trainingProfiles.position', 'Position')}: ${analysis.summary.position}`);
      }
      if (analysis.summary.keys.length > 0) {
        detailParts.push(`${analysis.summary.keys.length} ${t('trainingProfiles.fieldsLabel', 'fields')}`);
      }

      return renderValidationSuccess(
        t('trainingProfiles.spotlightTargetValid', 'Valid spotlight'),
        detailParts.join(' · ') || t('trainingProfiles.spotlightTargetValidHelp', 'Ready to use'),
        { compact }
      );
    }

    return (
      <Alert
        showIcon
        type="error"
        className={`${styles.conditionAstStatus}${compact ? ` ${styles.conditionAstStatusCompact}` : ''}`}
        message={t('trainingProfiles.spotlightTargetInvalid', 'Spotlight target is invalid')}
        description={analysis.error}
      />
    );
  }, [renderValidationSuccess, t]);

  const renderSpotlightTargetSummary = useCallback((analysis) => {
    if (!analysis?.parsed || analysis.status === 'empty' || analysis.status === 'invalid' || analysis.status === 'invalid-json') {
      return null;
    }

    const selectedOption = AVAILABLE_SPOTLIGHT_SELECTORS.find((entry) => entry.selector === analysis.parsed.selector) || null;
    const summaryRows = [
      {
        label: t('trainingProfiles.spotlightTargetSelectorLabel', 'Target'),
        value: selectedOption?.name || analysis.parsed.displayName || analysis.parsed.selector || t('trainingProfiles.noneLabel', 'None'),
      },
      {
        label: t('trainingProfiles.position', 'Position'),
        value: analysis.parsed.position || t('trainingProfiles.noneLabel', 'None'),
      },
      {
        label: t('trainingProfiles.spotlightTargetDisplayNameLabel', 'Display name'),
        value: analysis.parsed.displayName || t('trainingProfiles.noneLabel', 'None'),
      },
      {
        label: t('trainingProfiles.spotlightTargetFallbackTitle', 'Fallback rectangle'),
        value: analysis.summary.hasFallbackRect ? t('common.yes', 'Yes') : t('common.no', 'No'),
      },
    ];

    return (
      <div className={styles.spotlightBuilderSummaryCard}>
        {summaryRows.map((row) => (
          <div key={row.label} className={styles.spotlightBuilderSummaryRow}>
            <span>{row.label}</span>
            <strong>{row.value}</strong>
          </div>
        ))}
      </div>
    );
  }, [t]);

  const renderSpotlightTargetCard = useCallback((value) => {
    const analysis = analyzeSpotlightTargetValue(value);
    const isFilled = Boolean(analysis?.parsed);

    return (
      <div className={`${styles.tutorialContentCard} ${styles.tutorialContentCardWide}${!isFilled ? ` ${styles.tutorialContentCardEmpty}` : ''}`}>
        <div className={styles.tutorialContentCardHeader}>
          <div className={styles.tutorialContentLabel}>{t('trainingProfiles.spotlightTargetLabel', 'Spotlight target')}</div>
        </div>
        {renderSpotlightTargetStatus(analysis, { compact: true })}
        {isFilled ? (
          renderSpotlightTargetSummary(analysis)
        ) : (
          <p className={`${styles.tutorialContentPreview} ${styles.tutorialContentPreviewEmpty}`}>
            {t('trainingProfiles.noSpotlightTarget', 'No spotlight target has been configured for this step yet.')}
          </p>
        )}
      </div>
    );
  }, [renderSpotlightTargetStatus, renderSpotlightTargetSummary, t]);

  const renderStepCard = useCallback((quest, step) => {
    const spotlightPreview = getJsonPreview(step.spotlightTarget);

    const extra = (
      <Space className={styles.tutorialStepActions} wrap onClick={(e) => e.stopPropagation()}>
        <Button size="small" icon={<AimOutlined />} onClick={() => handleOpenStepSpotlightBuilder(quest, step)}>
          {t('trainingProfiles.configureSpotlight', 'Configure spotlight')}
        </Button>
        <Button size="small" icon={<EditOutlined />} onClick={() => openStepModal(quest, step)}>
          {t('trainingProfiles.editStep', 'Edit step')}
        </Button>
        <Popconfirm
          title={t('trainingProfiles.deleteStepConfirm', 'Delete this step?')}
          onConfirm={() => handleDeleteStep(step)}
        >
          <Button size="small" danger icon={<DeleteOutlined />}>
            {t('common.delete', 'Delete')}
          </Button>
        </Popconfirm>
      </Space>
    );

    const header = (
      <div className={styles.tutorialStepHeader}>
        <div className={styles.tutorialStepIndex}>{step.orderIndex}</div>
        <div className={styles.tutorialStepHeaderText}>
          <div className={styles.tutorialStepTitleRow}>
            <h4 className={styles.tutorialStepTitle}>{step.taskTitle}</h4>
          </div>
        </div>
      </div>
    );

    return (
      <Collapse.Panel key={step.id.toString()} header={header} extra={extra} className={styles.tutorialStepCard}>
        <div className={styles.tutorialSection}>
          <div className={styles.tutorialSectionTitle}>{t('trainingProfiles.stepDetails', 'Step details')}</div>
          <div className={styles.tutorialMetaGrid}>
            {renderMetaCard(t('trainingProfiles.stepKey', 'Step key'), step.stepKey)}
            {renderMetaCard(
              t('trainingProfiles.spotlightTarget', 'Spotlight target JSON'),
              spotlightPreview ? t('trainingProfiles.configured', 'Configured') : t('trainingProfiles.notConfigured', 'Not configured')
            )}
          </div>
        </div>

        <div className={styles.tutorialSection}>
          <div className={styles.tutorialSectionTitle}>{t('trainingProfiles.stepContent', 'Step content')}</div>
          <div className={styles.tutorialContentGrid}>
            {renderContentCard(t('trainingProfiles.instruction', 'Instruction'), step.instructionRichContent)}
            {renderContentCard(t('trainingProfiles.detailedGuidance', 'Detailed guidance'), step.detailedGuidanceRichContent)}
            {renderContentCard(t('trainingProfiles.successIndicator', 'Success indicator'), step.successIndicatorRichContent)}
            {renderSpotlightTargetCard(
              step.spotlightTarget,
              `${step.taskTitle} · ${t('trainingProfiles.spotlightTargetGuide', 'Spotlight target guide')}`
            )}
          </div>
        </div>
      </Collapse.Panel>
    );
  }, [handleDeleteStep, handleOpenStepSpotlightBuilder, openStepModal, renderContentCard, renderMetaCard, renderSpotlightTargetCard, t]);

  const renderQuestCard = useCallback((quest) => {
    const steps = quest.steps || [];

    const extra = (
      <Space className={styles.tutorialQuestActions} wrap onClick={(e) => e.stopPropagation()}>
        <Button type="primary" size='small' icon={<PlusOutlined />} onClick={() => openStepModal(quest)}>
          {t('trainingProfiles.addStep', 'Add step')}
        </Button>
        <Button size='small' icon={<EditOutlined />} onClick={() => openQuestModal(quest)}>
          {t('trainingProfiles.editQuest', 'Edit quest')}
        </Button>
        <Popconfirm
          title={t('trainingProfiles.deleteQuestConfirm', 'Delete this tutorial quest?')}
          onConfirm={() => handleDeleteQuest(quest)}
          onCancel={(e) => e.stopPropagation()}
        >
          <Button danger size='small' icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()}>
            {t('trainingProfiles.deleteQuest', 'Delete quest')}
          </Button>
        </Popconfirm>
      </Space>
    );

    const header = (
      <div className={styles.tutorialQuestTitleBlock}>
        <div className={styles.tutorialQuestHeaderText}>
          <div className={styles.tutorialQuestTitleRow}>
            <h3 className={styles.tutorialQuestTitle}>{quest.title}</h3>
          </div>
        </div>
      </div>
    );

    return (
      <Collapse.Panel key={quest.id.toString()} header={header} extra={extra} className={styles.tutorialQuestCard}>
        <div className={styles.tutorialSection}>
          <div className={styles.tutorialSectionTitle}>{t('trainingProfiles.questDetails', 'Quest details')}</div>
          <div className={styles.tutorialMetaGrid}>
            {renderMetaCard(t('trainingProfiles.category', 'Category'), quest.category)}
            {renderMetaCard(t('trainingProfiles.difficulty', 'Difficulty'), quest.difficulty)}
            {renderMetaCard(t('trainingProfiles.estimatedTime', 'Estimated time'), quest.estimatedTime ? `${quest.estimatedTime} min` : null)}
            {renderMetaCard(t('trainingProfiles.challenge', 'Challenge'), quest.isChallenge ? t('common.yes', 'Yes') : t('common.no', 'No'))}
            
          </div>
        </div>

        <div className={styles.tutorialSection}>
          <div className={styles.tutorialSectionTitle}>{t('trainingProfiles.questContent', 'Quest content')}</div>
          <div className={styles.tutorialContentGrid}>
            {renderContentCard(t('trainingProfiles.learningObjectives', 'Learning objectives'), quest.learningObjectivesRichContent)}
            {renderContentCard(t('trainingProfiles.scenario', 'Scenario'), quest.scenarioRichContent)}
            {renderContentCard(t('trainingProfiles.realWorldConnection', 'Real-world connection'), quest.realWorldConnectionRichContent)}
            {renderContentCard(t('trainingProfiles.completionDescription', 'Completion description'), quest.completionDescriptionRichContent)}
            {renderContentCard(t('trainingProfiles.knowledgeSynthesis', 'Knowledge synthesis'), quest.knowledgeSynthesisRichContent)}
            {renderContentCard(t('trainingProfiles.practicalTip', 'Practical tip'), quest.practicalTipRichContent)}
            {renderConditionAstCard(quest.conditionAst, `${quest.title} · ${t('trainingProfiles.conditionAstGuide', 'Condition AST guide')}`)}
          </div>
        </div>

        <div className={styles.tutorialSection}>
          <div className={styles.tutorialSectionHeader}>
            <div>
              <div className={styles.tutorialSectionTitle}>{t('trainingProfiles.stepsTitle', 'Steps')}</div>
              <p className={styles.tutorialSectionBody}>{t('trainingProfiles.stepsHelp', 'Review each learner step, its guidance content, success criteria, and spotlight targeting at a glance.')}</p>
            </div>
            <div className={styles.tutorialSectionCount}>{steps.length} {t('trainingProfiles.stepsLabel', 'steps')}</div>
          </div>

          {steps.length > 0 ? (
            <Collapse defaultActiveKey={steps.map(s => s.id.toString())} className={styles.tutorialCollapse} bordered={false}>
              {steps.map(step => renderStepCard(quest, step))}
            </Collapse>
          ) : (
            <div className={styles.emptyStateBox}>
              <div className={styles.emptyStateTitle}>{t('trainingProfiles.noSteps', 'No steps yet')}</div>
              <p className={styles.emptyStateBody}>{t('trainingProfiles.noStepsHelp', 'Add the first step to define the learner action, guidance, and success criteria for this quest.')}</p>
            </div>
          )}
        </div>
      </Collapse.Panel>
    );
  }, [handleDeleteQuest, openQuestModal, openStepModal, renderConditionAstCard, renderContentCard, renderMetaCard, renderStepCard, t]);

  const tabItems = [
    {
      key: 'profile',
      label: t('trainingProfiles.profileTab', 'Profile'),
      children: (
        <Form form={profileForm} layout="vertical">
          <Form.Item
            name="name"
            label={t('trainingProfiles.name', 'Name')}
            rules={[{ required: true, message: t('trainingProfiles.nameRequired', 'Name is required') }]}
          >
            <Input placeholder={t('trainingProfiles.namePlaceholder', 'Installer beginner profile')} />
          </Form.Item>

          <Form.Item
            name="countryId"
            label={t('trainingProfiles.language', 'Training language')}
            rules={[{ required: true, message: t('trainingProfiles.languageRequired', 'Training language is required') }]}
          >
            <Select
              showSearch
              loading={referenceLoading}
              optionFilterProp="label"
              options={countryOptions}
              placeholder={t('trainingProfiles.languagePlaceholder', 'Select the training language')}
            />
          </Form.Item>

          <div className={styles.supportingTextBlock}>
            <div className={styles.supportingTextTitle}>{t('trainingProfiles.languageInfoTitle', 'Language is driven by the country catalog')}</div>
            <p className={styles.supportingTextBody}>{t('trainingProfiles.languageInfo', 'Choose the catalog entry that represents the training language for this profile.')}</p>
          </div>

          <Form.Item name="additionalTextRichContent" label={t('trainingProfiles.description', 'Profile description') }>
            <RichTextEditor
              parentType="TrainingProfile"
              parentId={profileId}
              maxLength={5000}
              placeholder={t('trainingProfiles.descriptionPlaceholder', 'Describe the learner context and training goal...')}
            />
          </Form.Item>
        </Form>
      ),
    },
    {
      key: 'configuration',
      label: t('trainingProfiles.configurationTab', 'Configuration'),
      children: (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div className={styles.supportingTextBlock}>
            <div className={styles.supportingTextTitle}>{t('trainingProfiles.configBuilderTitle', 'Visual training configuration builder')}</div>
            <p className={styles.supportingTextBody}>{t('trainingProfiles.configBuilderDescription', 'Set what learners can see, edit, start with, or keep fixed in the simulator.')}</p>
          </div>

          <Collapse
            className={styles.configurationCollapse}
            defaultActiveKey={TRAINING_PROFILE_CONFIGURATION_GROUPS.map(group => group.id)}
            bordered={false}
          >
            {TRAINING_PROFILE_CONFIGURATION_GROUPS.map(group => (
              <Collapse.Panel
                key={group.id}
                header={(
                  <div className={styles.configurationGroupHeader}>
                    <div className={styles.configurationGroupHeaderText}>
                      <div className={styles.configurationGroupTitle}>{t(group.titleKey || group.title, group.title)}</div>
                      <div className={styles.configurationGroupDescription}>{t(group.descriptionKey || group.description, group.description)}</div>
                      <div className={styles.configurationGroupCount}>{group.items.length} {t('trainingProfiles.controls', 'controls')}</div>
                    </div>
                  </div>
                )}
                className={styles.configurationGroupPanel}
              >
                <div className={styles.configurationList}>
                  {group.items.map((item, index) => {
                    const state = configurationDraft[item.id] || {};
                    const showPreviewImage = Boolean(item.helpImageSrc) || item.showImagePlaceholder !== false;

                    return (
                      <React.Fragment key={item.id}>
                        {index > 0 && <Divider className={styles.configurationDivider} />}
                        <div className={styles.configurationItem}>
                          <div className={styles.configurationPreviewRow}>
                            {showPreviewImage && (
                              <div className={styles.configurationImageFrame}>
                                {item.helpImageSrc ? (
                                  <img
                                    src={item.helpImageSrc}
                                    alt={t(item.titleKey || item.title, item.title)}
                                    className={styles.configurationHelpImage}
                                  />
                                ) : (
                                  <div className={styles.configurationImagePlaceholder}>
                                    <PictureOutlined />
                                    <div className={styles.configurationImageText}>
                                      <span>{t('trainingProfiles.imagePlaceholder', 'Image placeholder')}</span>
                                      <small>{t('trainingProfiles.imagePlaceholderHelp', 'Add a reference screenshot later.')}</small>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            <div className={styles.configurationItemText}>
                              <h3>{t(item.titleKey || item.title, item.title)}</h3>
                              <p>{t(item.descriptionKey || item.description, item.description)}</p>
                            </div>
                          </div>

                          {(item.supportsVisibility || item.supportsEditability) && (
                            <div className={styles.configurationToggleLine}>
                              {item.supportsVisibility && (
                                <label className={styles.inlineToggle}>
                                  <span>{t('trainingProfiles.showInTraining', 'Visible')}</span>
                                  <Switch
                                    checked={state.visible !== false}
                                    onChange={checked => handleConfigurationChange(item.id, { visible: checked })}
                                  />
                                </label>
                              )}

                              {item.supportsEditability && (
                                <label className={styles.inlineToggle}>
                                  <span>{t('trainingProfiles.learnerEditable', 'Editable')}</span>
                                  <Switch
                                    checked={state.visible === false ? false : state.editable !== false}
                                    disabled={state.fixedEnabled || state.visible === false}
                                    onChange={checked => handleConfigurationChange(item.id, { editable: checked })}
                                  />
                                </label>
                              )}
                            </div>
                          )}

                          {item.valueKey && (
                            <div className={styles.configurationValueLine}>
                              <div className={styles.valueSetting}>
                                <label className={styles.inlineToggle}>
                                  <span>{t('trainingProfiles.applyDefault', 'Default')}</span>
                                  <Switch
                                    checked={!!state.defaultEnabled}
                                    disabled={!!state.fixedEnabled}
                                    onChange={checked => handleConfigurationChange(item.id, { defaultEnabled: checked })}
                                  />
                                </label>
                                {state.defaultEnabled && !state.fixedEnabled && (
                                  <div className={styles.inlineValueInput}>
                                    {renderConfigurationValueInput(item, state.defaultValue, nextValue => handleConfigurationChange(item.id, { defaultValue: nextValue }))}
                                  </div>
                                )}
                              </div>

                              {item.supportsFixedValue !== false && (
                                <div className={styles.valueSetting}>
                                  <label className={styles.inlineToggle}>
                                    <span>{t('trainingProfiles.fixValue', 'Fixed')}</span>
                                    <Switch
                                      checked={!!state.fixedEnabled}
                                      onChange={checked => handleConfigurationChange(item.id, {
                                        fixedEnabled: checked,
                                        editable: checked ? false : state.editable,
                                        defaultEnabled: checked ? false : state.defaultEnabled,
                                      })}
                                    />
                                  </label>
                                  {state.fixedEnabled && (
                                    <div className={styles.inlineValueInput}>
                                      {renderConfigurationValueInput(item, state.fixedValue, nextValue => handleConfigurationChange(item.id, { fixedValue: nextValue }))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </Collapse.Panel>
            ))}
          </Collapse>
        </Space>
      ),
    },
    {
      key: 'learning-resources',
      label: t('trainingProfiles.learningResourcesTab', 'Learning resources'),
      disabled: !profileId,
      children: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <input
            ref={resourceInputRef}
            type="file"
            accept=".pdf,.mp4,application/pdf,video/mp4"
            multiple
            style={{ display: 'none' }}
            onChange={handleLearningResourcesSelected}
          />

          <div className={styles.sectionHeaderRow}>
            <div className={styles.sectionHeaderMain}>
              <div className={styles.supportingTextBlock}>
                <div className={styles.supportingTextTitle}>{t('trainingProfiles.learningResourcesTitle', 'Attach profile learning resources')}</div>
                <p className={styles.supportingTextBody}>{t('trainingProfiles.learningResourcesHelp', 'Upload PDF handouts and MP4 videos. These files use the standard file storage lifecycle and are linked directly to this training profile.')}</p>
              </div>
            </div>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={handlePickLearningResources}
              loading={resourceUploadLoading}
            >
              {t('trainingProfiles.addLearningResource', 'Add resources')}
            </Button>
          </div>

          <div className={styles.resourcesPanel}>
            <div className={styles.resourcesPanelHeader}>
              <div>
                <div className={styles.quizPanelTitle}>{t('trainingProfiles.resourceLibrary', 'Profile resource library')}</div>
                <p className={styles.quizPanelDescription}>{t('trainingProfiles.resourceLibraryHelp', 'These are the files shown in the simulator Learning Resources drawer for this profile.')}</p>
              </div>
              <Tag color="blue">{learningResources.length} {t('trainingProfiles.resourcesCount', 'resources')}</Tag>
            </div>

            {learningResources.length > 0 ? (
              <div className={styles.resourceList}>
                {learningResources.map((resource) => {
                  const kind = getLearningResourceKind(resource);
                  const ResourceIcon = kind.icon;

                  return (
                    <div key={resource.id} className={styles.resourceCard}>
                      <div className={styles.resourceCardIconWrap} data-kind={kind.key}>
                        <ResourceIcon className={styles.resourceCardIcon} />
                      </div>

                      <div className={styles.resourceCardBody}>
                        <div className={styles.resourceCardTitleRow}>
                          <div className={styles.resourceCardTitle}>{resource.fileName}</div>
                        </div>

                        <div className={styles.resourceCardMeta}>
                          <span>{formatFileSize(resource.fileSizeBytes)}</span>
                          <span>{resource.fileExtension?.toUpperCase()?.replace('.', '') || t(kind.key === 'video' ? 'trainingProfiles.resourceTypeVideo' : 'trainingProfiles.resourceTypePdf', kind.label)}</span>
                        </div>
                      </div>

                      <Space>
                        <Tooltip title={t('trainingProfiles.openLearningResource', 'Open resource')}>
                          <Button
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => openLearningResource(resource)}
                          />
                        </Tooltip>
                        <Popconfirm
                          title={t('trainingProfiles.removeLearningResource', 'Remove learning resource?')}
                          okText={t('common.yes', 'Yes')}
                          cancelText={t('common.no', 'No')}
                          onConfirm={() => handleDeleteLearningResource(resource.id)}
                        >
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            loading={resourceDeleteLoadingId === resource.id}
                          />
                        </Popconfirm>
                      </Space>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={styles.emptyStateBox}>
                <div className={styles.emptyStateTitle}>{t('trainingProfiles.noLearningResources', 'No learning resources yet')}</div>
                <p className={styles.emptyStateBody}>{t('trainingProfiles.noLearningResourcesHelp', 'Upload PDF and MP4 files here to replace the generic simulator documents for this training profile.')}</p>
              </div>
            )}
          </div>
        </Space>
      ),
    },
    {
      key: 'tutorials',
      label: t('trainingProfiles.tutorialsTab', 'Tutorial quests'),
      disabled: !profileId,
      children: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div className={styles.sectionHeaderRow}>
            <div className={styles.sectionHeaderMain}>
              <div className={styles.supportingTextBlock}>
                <div className={styles.supportingTextTitle}>{t('trainingProfiles.tutorialsTab', 'Tutorial quests')}</div>
                <p className={styles.supportingTextBody}>{t('trainingProfiles.questHelp', 'Tutorial quests are stored in the database with RichContent and a safe JSON condition AST.')}</p>
              </div>
            </div>
            <Space wrap>
              <Button icon={<ImportOutlined />} onClick={() => setBulkImportModalOpen(true)}>
                {t('trainingProfiles.bulkImport.button', 'Bulk import')}
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openQuestModal()}>
                {t('trainingProfiles.addQuest', 'Add quest')}
              </Button>
            </Space>
          </div>
          {(profile?.tutorialQuests || []).length > 0 ? (
            <Collapse defaultActiveKey={(profile?.tutorialQuests || []).map(q => q.id.toString())} className={styles.tutorialCollapse} bordered={false}>
              {(profile?.tutorialQuests || []).map(renderQuestCard)}
            </Collapse>
          ) : (
            <Alert message={t('trainingProfiles.noQuests', 'No tutorial quests yet.')} />
          )}
        </Space>
      ),
    },
    {
      key: 'quizzes',
      label: t('trainingProfiles.quizzesTab', 'Skill quizzes'),
      disabled: !profileId,
      children: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div className={styles.sectionHeaderRow}>
            <div className={styles.sectionHeaderMain}>
              <div className={styles.supportingTextBlock}>
                <div className={styles.supportingTextTitle}>{t('trainingProfiles.quizAssignmentHelpTitle', 'Assign quiz collections')}</div>
                <p className={styles.supportingTextBody}>{t('trainingProfiles.quizAssignmentHelp', 'Pick from all published quiz collections and order the ones learners can launch from this profile.')}</p>
              </div>
            </div>
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveQuizAssignments} loading={saving}>
              {t('trainingProfiles.saveQuizAssignments', 'Save quiz assignments')}
            </Button>
          </div>

          <div className={styles.quizWorkspace}>
            <div className={styles.quizSelectionPanel}>
              <div className={styles.quizPanelHeaderRow}>
                <div>
                  <div className={styles.quizPanelTitle}>{t('trainingProfiles.selectedQuizCollections', 'Selected quiz collections')}</div>
                  <p className={styles.quizPanelDescription}>{t('trainingProfiles.selectedQuizCollectionsHelp', 'Set the learner-facing order for the assigned quiz collections.')}</p>
                </div>
                <Tag color="blue">{selectedQuizCollections.length} {t('trainingProfiles.selected', 'selected')}</Tag>
              </div>

              {selectedQuizCollections.length > 0 ? (
                <div className={styles.selectedQuizList}>
                  {selectedQuizCollections.map(({ quizCollectionId, collection }, index) => (
                    <div key={quizCollectionId} className={styles.selectedQuizCard}>
                      <div className={styles.selectedQuizOrder}>{index + 1}</div>
                      <div className={styles.selectedQuizText}>
                        <div className={styles.selectedQuizName}>{collection?.name || `${t('trainingProfiles.quizCollection', 'Quiz collection')} #${quizCollectionId}`}</div>
                        <div className={styles.selectedQuizMeta}>
                          <span>{collection?.publishedQuestionCount ?? collection?.questionCount ?? 0} {t('trainingProfiles.questions', 'questions')}</span>
                          <span>{collection?.topicCount ?? 0} {t('trainingProfiles.topics', 'topics')}</span>
                          <span>{collection?.ownerName || t('trainingProfiles.sharedPublishedQuiz', 'Published shared quiz')}</span>
                        </div>
                      </div>
                      <Space>
                        <Tooltip title={t('trainingProfiles.moveUp', 'Move up')}>
                          <Button
                            size="small"
                            icon={<ArrowUpOutlined />}
                            onClick={() => handleMoveQuizAssignment(quizCollectionId, 'up')}
                            disabled={index === 0}
                          />
                        </Tooltip>
                        <Tooltip title={t('trainingProfiles.moveDown', 'Move down')}>
                          <Button
                            size="small"
                            icon={<ArrowDownOutlined />}
                            onClick={() => handleMoveQuizAssignment(quizCollectionId, 'down')}
                            disabled={index === selectedQuizCollections.length - 1}
                          />
                        </Tooltip>
                        <Tooltip title={t('trainingProfiles.removeQuizCollection', 'Remove from profile')}>
                          <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleRemoveQuizAssignment(quizCollectionId)}
                          />
                        </Tooltip>
                      </Space>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyStateBox}>
                  <div className={styles.emptyStateTitle}>{t('trainingProfiles.noQuizAssignmentsYet', 'No quiz collections selected yet')}</div>
                  <p className={styles.emptyStateBody}>{t('trainingProfiles.noQuizAssignmentsHelp', 'Search the published quiz catalog below and add the collections that should be available in this training profile.')}</p>
                </div>
              )}
            </div>

            <div className={styles.quizLibraryPanel}>
              <div className={styles.quizPanelHeaderRow}>
                <div>
                  <div className={styles.quizPanelTitle}>{t('trainingProfiles.publishedQuizLibrary', 'Published quiz library')}</div>
                  <p className={styles.quizPanelDescription}>{t('trainingProfiles.publishedQuizLibraryHelp', 'Search all published quiz collections and add the ones that fit this profile.')}</p>
                </div>
              </div>

              <div className={styles.quizToolbar}>
                <Input.Search
                  allowClear
                  value={quizSearchText}
                  onChange={(event) => setQuizSearchText(event.target.value)}
                  placeholder={t('trainingProfiles.quizSearchPlaceholder', 'Search by quiz name or owner')}
                  className={styles.quizSearchInput}
                />
                <Tag>{filteredQuizCollections.length} {t('trainingProfiles.results', 'results')}</Tag>
              </div>

              <Table
                rowKey="id"
                size="small"
                loading={referenceLoading}
                dataSource={filteredQuizCollections}
                pagination={{ pageSize: 8, hideOnSinglePage: true }}
                columns={[
                  {
                    title: t('trainingProfiles.quizCollection', 'Quiz collection'),
                    dataIndex: 'name',
                    sorter: (a, b) => a.name.localeCompare(b.name),
                    render: (_, record) => (
                      <div className={styles.quizTableNameCell}>
                        <div className={styles.quizTableName}>{record.name}</div>
                        <div className={styles.quizTableSubline}>{record.ownerName || t('trainingProfiles.sharedPublishedQuiz', 'Published shared quiz')}</div>
                      </div>
                    ),
                  },
                  {
                    title: t('trainingProfiles.questions', 'Questions'),
                    dataIndex: 'publishedQuestionCount',
                    width: 110,
                    sorter: (a, b) => (a.publishedQuestionCount || 0) - (b.publishedQuestionCount || 0),
                    render: (_, record) => record.publishedQuestionCount ?? record.questionCount ?? 0,
                  },
                  {
                    title: t('trainingProfiles.topics', 'Topics'),
                    dataIndex: 'topicCount',
                    width: 90,
                    sorter: (a, b) => (a.topicCount || 0) - (b.topicCount || 0),
                  },
                  {
                    title: t('common.actions', 'Actions'),
                    width: 120,
                    render: (_, record) => {
                      const isSelected = quizAssignmentIds.includes(record.id);
                      return (
                        <Space>
                          <Tooltip title={t('trainingProfiles.openQuizCollection', 'Open quiz collection in a new window')}>
                            <Button size="small" icon={<EyeOutlined />} onClick={() => handleOpenQuizCollection(record.id)} />
                          </Tooltip>
                          {isSelected ? (
                            <Button size="small" onClick={() => handleRemoveQuizAssignment(record.id)}>
                              {t('trainingProfiles.remove', 'Remove')}
                            </Button>
                          ) : (
                            <Button type="primary" size="small" onClick={() => handleAddQuizAssignment(record.id)}>
                              {t('trainingProfiles.add', 'Add')}
                            </Button>
                          )}
                        </Space>
                      );
                    },
                  },
                ]}
              />
            </div>
          </div>
        </Space>
      ),
    },
  ];

  return (
    <PagesWrapper>
      <Spin spinning={loading}>
        <div className={styles.editorContainer}>
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/training-profiles')}>
                {t('common.back', 'Back')}
              </Button>
              <h1>
                {isNew ? t('trainingProfiles.createTitle', 'Create Training Profile') : profile?.name || t('trainingProfiles.editTitle', 'Edit Training Profile')}
              </h1>
              {profile && (
                <>
                  {profile.isPublished
                    ? <Tag color="success" icon={<CheckCircleOutlined />}>{t('trainingProfiles.published', 'Published')}</Tag>
                    : <Tag icon={<CloseCircleOutlined />}>{t('trainingProfiles.draft', 'Draft')}</Tag>}
                  {(profile.countryLanguage || profile.countryName) && <Tag color="blue">{profile.countryLanguage || profile.countryName}</Tag>}
                  <Tag color="green">{profile.tutorialQuests?.length || 0} quests</Tag>
                  <Tag color="purple">{profile.quizAssignments?.length || 0} quizzes</Tag>
                  {profile.addedByName && (
                    <Tooltip title={t('trainingProfiles.createdBy', 'Created by')}>
                      <Space size="small" className={styles.createdBy}>
                        <UserOutlined />
                        <Typography.Text type="secondary">{profile.addedByName}</Typography.Text>
                      </Space>
                    </Tooltip>
                  )}
                </>
              )}
            </div>

            <Space className={styles.headerActions} wrap>
              {profile?.isPublished && profileId && (
                <Tooltip title={t('trainingProfiles.openLearnerView', 'Open learner view')}>
                  <Button
                    icon={<EyeOutlined />}
                    onClick={() => window.open(`/simulations/heat-pump-training?profileId=${profileId}`, '_blank')}
                  >
                    {t('trainingProfiles.openLearnerView', 'Open learner view')}
                  </Button>
                </Tooltip>
              )}
              {profile?.isPublished && profileId && (
                <Tooltip title={t('trainingProfiles.copyLearnerLink', 'Copy learner link')}>
                  <Button icon={<CopyOutlined />} onClick={handleCopyLearnerLink}>
                    {t('trainingProfiles.copyLearnerLink', 'Copy learner link')}
                  </Button>
                </Tooltip>
              )}
              {profileId && (
                <Tooltip title={t('ownership.manageTitle', 'Manage ownership')}>
                  <Button icon={<ShareAltOutlined />} onClick={() => setOwnershipModalOpen(true)}>
                    {t('ownership.manage', 'Ownership')}
                  </Button>
                </Tooltip>
              )}
              {profileId && (
                <Button onClick={handleTogglePublish} loading={saving}>
                  {profile?.isPublished ? t('trainingProfiles.unpublish', 'Unpublish') : t('trainingProfiles.publish', 'Publish')}
                </Button>
              )}
              <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveProfile} loading={saving}>
                {t('common.save', 'Save')}
              </Button>
            </Space>
          </div>

          {ownership.ownershipInfo && (
            <AccessBanner
              accessLevel={ownership.ownershipInfo.accessLevel}
              ownerInfo={ownership.ownershipInfo.mainOwner}
              canEdit={ownership.ownershipInfo.canEdit}
              canDelete={ownership.ownershipInfo.canDelete}
            />
          )}

          {isNew && (
            <Alert
              type="warning"
              showIcon
              message={t('trainingProfiles.newProfileNotice', 'Save the profile first to enable tutorial quests, quiz assignments, and ownership tools.')}
            />
          )}

          <Card>
            <Tabs items={tabItems} />
          </Card>
        </div>
      </Spin>

      <Modal
        title={editingQuest ? t('trainingProfiles.editQuest', 'Edit quest') : t('trainingProfiles.addQuest', 'Add quest')}
        open={questModalOpen}
        onCancel={() => setQuestModalOpen(false)}
        onOk={handleSaveQuest}
        confirmLoading={saving}
        width={920}
        destroyOnClose
      >
        <Form form={questForm} layout="vertical" className={styles.questModalForm}>
          <div className={styles.questModalIntro}>
            <div className={styles.questModalIntroTitle}>{t('trainingProfiles.questSetupTitle', 'Quest setup')}</div>
            <p className={styles.questModalIntroBody}>{t('trainingProfiles.questSetupHelp', 'Keep the basics compact here, then use the tabs below to edit the content without one long scrolling form.')}</p>
          </div>

          <div className={styles.questModalSection}>
            <div className={styles.questModalSectionTitle}>{t('trainingProfiles.questDetails', 'Quest details')}</div>
            <div className={styles.questModalDetailsGrid}>
              <Form.Item name="orderIndex" label="#" className={styles.questModalCompactField}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="title" label={t('trainingProfiles.questTitle', 'Quest title')} rules={[{ required: true }]} className={styles.questModalTitleField}>
                <Input />
              </Form.Item>
              <Form.Item name="category" label={t('trainingProfiles.category', 'Category')} className={styles.questModalCompactField}>
                <Input />
              </Form.Item>
              <Form.Item name="difficulty" label={t('trainingProfiles.difficulty', 'Difficulty')} className={styles.questModalCompactField}>
                <Input />
              </Form.Item>
              <Form.Item name="estimatedTime" label={t('trainingProfiles.estimatedTime', 'Estimated time')} className={styles.questModalCompactField}>
                <Input />
              </Form.Item>
              <Form.Item
                name="iconKey"
                label={t('trainingProfiles.iconKey', 'Icon key')}
                className={styles.questModalCompactField}
              >
                <Select
                  allowClear
                  showSearch
                  placeholder={t('trainingProfiles.selectOptionalIcon', 'Select an optional icon')}
                  options={questIconOptions}
                  filterOption={(input, option) => option?.searchLabel?.includes(input.toLowerCase())}
                />
              </Form.Item>
              <Form.Item name="isChallenge" label={t('trainingProfiles.challenge', 'Challenge')} valuePropName="checked" className={styles.questModalSwitchField}>
                <Switch />
              </Form.Item>
            </div>
          </div>

          <div className={styles.questModalSection}>
            <Tabs
              className={styles.questModalTabs}
              items={[
                {
                  key: 'learning-objectives',
                  label: t('trainingProfiles.learningObjectives', 'Learning objectives'),
                  children: (
                    <Form.Item name="learningObjectivesRichContent" className={styles.questModalEditorField}>
                      <RichTextEditor {...MODAL_EDITOR_HEIGHT_PROPS} parentType="TrainingProfile" parentId={profileId} />
                    </Form.Item>
                  ),
                },
                {
                  key: 'scenario',
                  label: t('trainingProfiles.scenario', 'Scenario'),
                  children: (
                    <Form.Item name="scenarioRichContent" className={styles.questModalEditorField}>
                      <RichTextEditor {...MODAL_EDITOR_HEIGHT_PROPS} parentType="TrainingProfile" parentId={profileId} />
                    </Form.Item>
                  ),
                },
                {
                  key: 'real-world-connection',
                  label: t('trainingProfiles.realWorldConnection', 'Real-world connection'),
                  children: (
                    <Form.Item name="realWorldConnectionRichContent" className={styles.questModalEditorField}>
                      <RichTextEditor {...MODAL_EDITOR_HEIGHT_PROPS} parentType="TrainingProfile" parentId={profileId} />
                    </Form.Item>
                  ),
                },
                {
                  key: 'completion-description',
                  label: t('trainingProfiles.completionDescription', 'Completion description'),
                  children: (
                    <Form.Item name="completionDescriptionRichContent" className={styles.questModalEditorField}>
                      <RichTextEditor {...MODAL_EDITOR_HEIGHT_PROPS} parentType="TrainingProfile" parentId={profileId} />
                    </Form.Item>
                  ),
                },
                {
                  key: 'knowledge-synthesis',
                  label: t('trainingProfiles.knowledgeSynthesis', 'Knowledge synthesis'),
                  children: (
                    <Form.Item name="knowledgeSynthesisRichContent" className={styles.questModalEditorField}>
                      <RichTextEditor {...MODAL_EDITOR_HEIGHT_PROPS} parentType="TrainingProfile" parentId={profileId} />
                    </Form.Item>
                  ),
                },
                {
                  key: 'practical-tip',
                  label: t('trainingProfiles.practicalTip', 'Practical tip'),
                  children: (
                    <Form.Item name="practicalTipRichContent" className={styles.questModalEditorField}>
                      <RichTextEditor {...MODAL_EDITOR_HEIGHT_PROPS} parentType="TrainingProfile" parentId={profileId} />
                    </Form.Item>
                  ),
                },
              ]}
            />
          </div>

          <div className={styles.questModalSection}>
            <div className={styles.questModalSectionHeader}>
              <div>
                <div className={styles.questModalSectionTitle}>{t('trainingProfiles.conditionAst', 'Condition AST JSON')}</div>
                <p className={styles.questModalSectionBody}>{t('trainingProfiles.conditionAstHelp', 'Use a backend-validated JSON AST to decide when this quest becomes available or complete in the simulator.')}</p>
              </div>
              <Space wrap>
                <Button size="small" onClick={handleUseStarterConditionAst}>
                  {t('trainingProfiles.useStarterExample', 'Use starter example')}
                </Button>
                <Button size="small" onClick={handleUseTwoConditionAst}>
                  {t('trainingProfiles.useTwoConditionExample', 'Use two-condition example')}
                </Button>
                <Button size="small" onClick={handleUseCrossFieldConditionAst}>
                  {t('trainingProfiles.useCrossFieldExample', 'Use cross-field example')}
                </Button>
                <Button size="small" onClick={handleFormatConditionAst}>
                  {t('trainingProfiles.formatJson', 'Format JSON')}
                </Button>
              </Space>
            </div>

            {renderConditionAstStatus(conditionAstDraftAnalysis)}

            <Form.Item
              name="conditionAstText"
              className={styles.questModalJsonField}
              rules={[{ validator: validateConditionAstField }]}
            >
              <TextArea rows={8} spellCheck={false} placeholder={formatJson(DEFAULT_CONDITION)} />
            </Form.Item>

            <Collapse className={styles.questModalHelpCollapse} bordered={false}>
              <Collapse.Panel
                key="condition-ast-help"
                header={(
                  <span className={styles.questModalHelpCollapseHeader}>
                    <QuestionCircleOutlined />
                    {t('trainingProfiles.conditionAstGuide', 'Condition AST guide')}
                  </span>
                )}
              >
                {renderConditionAstReference(conditionAstDraftAnalysis, { showExamples: true })}
              </Collapse.Panel>
            </Collapse>
          </div>
        </Form>
      </Modal>

      <Modal
        title={conditionAstHelpModalState.title || t('trainingProfiles.conditionAstGuide', 'Condition AST guide')}
        open={conditionAstHelpModalState.open}
        onCancel={handleCloseConditionAstHelpModal}
        footer={null}
        width={980}
        destroyOnClose
      >
        <div className={styles.conditionAstHelpModalBody}>
          {renderConditionAstStatus(conditionAstHelpModalState.analysis)}
          {renderConditionAstReference(conditionAstHelpModalState.analysis, { showExamples: true })}
        </div>
      </Modal>

      <Modal
        title={spotlightBuilderStepContext?.step?.taskTitle
          ? `${spotlightBuilderStepContext.step.taskTitle} · ${t('trainingProfiles.configureSpotlight', 'Configure spotlight')}`
          : t('trainingProfiles.configureSpotlight', 'Configure spotlight')}
        open={spotlightBuilderOpen}
        onCancel={handleCloseSpotlightBuilder}
        onOk={handleApplySpotlightBuilder}
        confirmLoading={saving}
        width={980}
        destroyOnClose={false}
      >
        <Form form={spotlightBuilderForm} layout="vertical" className={styles.spotlightBuilderForm}>
          <div className={styles.questModalSection}>
            <div className={styles.questModalSectionHeader}>
              <div>
                <div className={styles.questModalSectionTitle}>{t('trainingProfiles.spotlightTargetSelectorLabel', 'Target')}</div>
                <p className={styles.questModalSectionBody}>{t('trainingProfiles.spotlightBuilderSelectorHelp', 'Choose an existing simulator target or pick one from the selector library below.')}</p>
              </div>
            </div>

            <Form.Item name="selector" label={t('trainingProfiles.spotlightTargetSelectorLabel', 'Target')}>
              <Select
                allowClear
                showSearch
                placeholder={t('trainingProfiles.selectSpotlightTarget', 'Select a spotlight target')}
                options={spotlightSelectorOptions}
                filterOption={(input, option) => option?.searchLabel?.includes(input.toLowerCase())}
                onChange={handleSelectSpotlightBuilderSelector}
              />
            </Form.Item>

            <div className={styles.spotlightBuilderSelectorGrid}>
              {spotlightSelectorOptions.map((entry) => (
                <button
                  key={entry.value}
                  type="button"
                  className={`${styles.spotlightBuilderSelectorCard}${spotlightBuilderSelector === entry.value ? ` ${styles.spotlightBuilderSelectorCardActive}` : ''}`}
                  onClick={() => handleSelectSpotlightBuilderSelector(entry.value)}
                >
                  {entry.imageSrc && (
                    <span className={styles.spotlightBuilderSelectorCardPreview}>
                      <img src={entry.imageSrc} alt={entry.imageAlt || entry.name} />
                    </span>
                  )}
                  <span className={styles.spotlightBuilderSelectorCardText}>
                    <span className={styles.spotlightBuilderSelectorCardMeta}>{entry.categoryLabel}</span>
                    <strong>{entry.name}</strong>
                    <code>{entry.value}</code>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className={styles.questModalSection}>
            <div className={styles.questModalSectionTitle}>{t('trainingProfiles.spotlightBuilderPlacementTitle', 'Placement')}</div>
            <Form.Item name="position" style={{ marginBottom: 0 }}>
              <Radio.Group className={styles.spotlightBuilderPositionGroup} optionType="button" buttonStyle="solid">
                {SPOTLIGHT_POSITION_DEFINITIONS.map((position) => (
                  <Radio.Button key={position.key} value={position.key}>{position.key}</Radio.Button>
                ))}
              </Radio.Group>
            </Form.Item>

            <Form.Item name="displayName" label={t('trainingProfiles.spotlightTargetDisplayNameLabel', 'Display name')} style={{ marginBottom: 0 }}>
              <Input placeholder={spotlightBuilderSelectedOption?.name || t('trainingProfiles.spotlightTargetDisplayNamePlaceholder', 'Control panel')} />
            </Form.Item>
          </div>

          <div className={styles.questModalSection}>
            <div className={styles.questModalSectionHeader}>
              <div>
                <div className={styles.questModalSectionTitle}>{t('trainingProfiles.spotlightTargetFallbackTitle', 'Fallback rectangle')}</div>
                <p className={styles.questModalSectionBody}>{t('trainingProfiles.spotlightBuilderFallbackHelp', 'Use a manual fallback area when the selector is unstable or the target is not easy to query in the DOM.')}</p>
              </div>
              <Form.Item name="useFallbackRect" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Switch />
              </Form.Item>
            </div>

            {spotlightBuilderUseFallbackRect && (
              <div className={styles.spotlightBuilderFallbackGrid}>
                <Form.Item name="fallbackX" label="X" rules={[{ required: true }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="fallbackY" label="Y" rules={[{ required: true }]}>
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="fallbackWidth" label={t('trainingProfiles.width', 'Width')} rules={[{ required: true }]}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
                <Form.Item name="fallbackHeight" label={t('trainingProfiles.height', 'Height')} rules={[{ required: true }]}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </div>
            )}
          </div>
        </Form>
      </Modal>

      <Modal
        title={editingStep ? t('trainingProfiles.editStep', 'Edit step') : t('trainingProfiles.addStep', 'Add step')}
        open={stepModalOpen}
        onCancel={() => setStepModalOpen(false)}
        onOk={handleSaveStep}
        confirmLoading={saving}
        width={900}
        destroyOnClose
      >
        <Form form={stepForm} layout="vertical" className={styles.questModalForm}>
          <div className={styles.questModalSection}>
            <div className={styles.questModalSectionTitle}>{t('trainingProfiles.stepDetails', 'Step details')}</div>
            <div className={styles.questModalDetailsGrid}>
              <Form.Item name="orderIndex" label="#" className={styles.questModalCompactField}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="stepKey" label={t('trainingProfiles.stepKey', 'Step key')} className={styles.questModalCompactField}>
                <Input />
              </Form.Item>
              <Form.Item name="taskTitle" label={t('trainingProfiles.taskTitle', 'Task title')} rules={[{ required: true }]} className={styles.questModalTitleField}>
                <Input />
              </Form.Item>
            </div>
          </div>

          <div className={styles.questModalSection}>
            <Tabs
              className={styles.questModalTabs}
              items={[
                {
                  key: 'instruction',
                  label: t('trainingProfiles.instruction', 'Instruction'),
                  children: (
                    <Form.Item name="instructionRichContent" className={styles.questModalEditorField}>
                      <RichTextEditor {...MODAL_EDITOR_HEIGHT_PROPS} parentType="TrainingProfile" parentId={profileId} />
                    </Form.Item>
                  ),
                },
                {
                  key: 'detailed-guidance',
                  label: t('trainingProfiles.detailedGuidance', 'Detailed guidance'),
                  children: (
                    <Form.Item name="detailedGuidanceRichContent" className={styles.questModalEditorField}>
                      <RichTextEditor {...MODAL_EDITOR_HEIGHT_PROPS} parentType="TrainingProfile" parentId={profileId} />
                    </Form.Item>
                  ),
                },
                {
                  key: 'success-indicator',
                  label: t('trainingProfiles.successIndicator', 'Success indicator'),
                  children: (
                    <Form.Item name="successIndicatorRichContent" className={styles.questModalEditorField}>
                      <RichTextEditor {...MODAL_EDITOR_HEIGHT_PROPS} parentType="TrainingProfile" parentId={profileId} />
                    </Form.Item>
                  ),
                },
              ]}
            />
          </div>

          <Form.Item name="spotlightTargetText" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <OwnershipManager
        open={ownershipModalOpen}
        onClose={() => setOwnershipModalOpen(false)}
        ownershipInfo={ownership.ownershipInfo}
        canManageOwnership={ownership.canManageOwnership}
        onShare={ownership.shareWith}
        onRevoke={ownership.revoke}
        onGetAvailableUsers={ownership.getAvailableUsers}
        availableUsers={ownership.availableUsers}
        loading={ownership.loading || ownership.loadingAvailableUsers}
      />

      <TrainingProfileBulkImportModal
        open={bulkImportModalOpen}
        profileId={profileId}
        onClose={() => setBulkImportModalOpen(false)}
        onSuccess={loadProfile}
      />
    </PagesWrapper>
  );
};

export default TrainingProfileEditor;
